import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, GROUPS, BIKE_STATUS } from "../config";
import { LogOut, RefreshCw, Pencil, Plus, Trash2, X, Check, Ban, TicketPercent, Star, Sparkles, Loader2, KeyRound, Bike, Activity, CalendarClock, Euro, Trophy, ArrowRightLeft, ArrowUp, ArrowDown, Eye, Gauge, Store, Globe, Handshake, Power, UserCog, BookOpen, MailCheck, HardDrive, Monitor, Wrench, FileText, Bell } from "lucide-react";
import { FaGoogle } from "react-icons/fa";
import SiempriaCredit from "../components/SiempriaCredit";
import HelpModal from "../components/HelpModal";
import CustomersTab from "../components/admin/CustomersTab";
import PartnersTab from "../components/admin/PartnersTab";
import LogsTab from "../components/admin/LogsTab";
import CalendarTab from "../components/admin/CalendarTab";
import ManualBookingForm from "../components/admin/ManualBookingForm";
import BookingDetailsModal from "../components/admin/BookingDetailsModal";
import ContentTab from "../components/admin/ContentTab";
import { ContractButton, CommissionModal, DepositModal, DEPOSIT_BADGE } from "../components/admin/BookingOps";

const STATUS_LABELS = {
  pending: "Pendiente", deposit_paid: "Depósito pagado", confirmed: "Confirmada",
  completed: "Completada", cancelled: "Cancelada",
};
const STATUS_COLORS = {
  pending: "bg-brand text-topacio-ink",
  deposit_paid: "bg-topacio-green text-white",
  confirmed: "bg-topacio-green text-white",
  completed: "bg-zinc-200 text-topacio-faded",
  cancelled: "bg-topacio-red text-white",
};
const PHOTO_STATUS = {
  pending: ["Pendiente", "bg-brand text-topacio-ink"],
  approved: ["Publicada", "bg-topacio-green text-white"],
  rejected: ["Rechazada", "bg-topacio-red text-white"],
};
const CATEGORIES = ["scooter", "naked", "trail", "neo-retro", "supermotard", "mini-naked"];
// estados que el equipo puede fijar a mano por unidad (sin "alquilada"/"retornada", que son automáticos)
const UNIT_STATUS_OPTIONS = Object.entries(BIKE_STATUS).filter(([k]) => !["alquilada", "retornada"].includes(k));
const ROLE_LABELS = { superadmin: "Superadmin", admin: "Administrador", viewer: "Consulta", vendedor: "Vendedor", entrega: "Entrega", mecanico: "Mecánico", lavamotos: "Lavamotos" };
const ROLE_COLORS = { superadmin: "bg-topacio-ink text-brand", admin: "bg-topacio-red text-white", viewer: "bg-white text-topacio-ink", vendedor: "bg-brand text-topacio-ink", entrega: "bg-topacio-green text-white", mecanico: "bg-orange-500 text-white", lavamotos: "bg-sky-600 text-white" };
// "hace X" relativo en español para el último cambio de estado de la moto
const fmtAgo = (iso) => {
  try {
    const d = new Date(iso); const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "hace un momento";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    if (diff < 2592000) return `hace ${Math.floor(diff / 86400)} d`;
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};
const statusActionLabel = (status) => status === "revisada" ? "Revisada por" : status === "ok" ? "Marcada OK por" : "Estado por";
const EMPTY_BIKE = { name: "", category: "scooter", cc: 125, license: "A1 / B+3", image: "", group: "C",
  price_1d: 39, price_3d: 111, price_7d: 238, deposit: 150, automatic: false, active: true, coming_soon: false,
  topcase_included: false, stock: 1, matricula: "", bastidor: "", keyless_included: false };

function BikeEditor({ bike, groups, onClose, onSave, onManageGroups, headers }) {
  const [form, setForm] = useState(bike || EMPTY_BIKE);
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const num = (v) => parseFloat(v) || 0;
  const setUnit = (i, k, v) => {
    const units = Array.from({ length: Math.max(form.stock || 1, 1) },
      (_, idx) => form.units?.[idx] || { plate: "", internal_name: "", bastidor: "", status: "ok" });
    units[i] = { ...units[i], [k]: v };
    setForm({ ...form, units });
  };
  const groupList = groups && groups.length
    ? groups
    : Object.entries(GROUPS).map(([letter, g]) => ({ letter, ...g }));

  const save = async () => {
    setBusy(true);
    try {
      await onSave(form, photoFile);
      if (form.slug && (form.stock || 1) > 1) {
        const units = Array.from({ length: form.stock },
          (_, idx) => form.units?.[idx] || { plate: "", internal_name: "", bastidor: "", status: "ok" });
        try { await axios.patch(`${API}/admin/bikes/${form.slug}/units`, { units }, { headers }); } catch { /* no bloquear el guardado */ }
      }
      onClose();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="bike-editor-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display font-bold text-topacio-ink">{bike ? "Editar moto" : "Nueva moto"}</h3>
          <button data-testid="bike-editor-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nombre</label>
            <input data-testid="bike-field-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Categoría</label>
            <select data-testid="bike-field-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="input-brutal mt-1">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Grupo (tarifa)</label>
            <select data-testid="bike-field-group" value={form.group} onChange={(e) => {
              const g = e.target.value;
              const gp = groupList.find((x) => x.letter === g);
              setForm(gp
                ? { ...form, group: g, license: gp.license, price_1d: gp.price_1d, price_3d: gp.price_3d, price_7d: gp.price_7d, deposit: gp.deposit }
                : { ...form, group: g });
            }} className="input-brutal mt-1">
              {groupList.map((g) => (
                <option key={g.letter} value={g.letter}>Grupo {g.letter} · {g.price_1d}€/día · fianza {g.deposit}€ · carnet {g.license}</option>
              ))}
            </select>
            <p className="text-[10px] text-topacio-faded font-semibold mt-1.5">El grupo rellena precios, fianza y carnet automáticamente (puedes ajustarlos después).</p>
            {onManageGroups && (
              <button type="button" data-testid="manage-groups-link" onClick={onManageGroups}
                className="mt-1.5 text-[11px] font-extrabold text-topacio-red underline underline-offset-2 hover:text-topacio-ink transition-colors">
                Crear / modificar grupos y precios →
              </button>
            )}
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">cc</label>
            <input data-testid="bike-field-cc" type="number" value={form.cc} onChange={(e) => set("cc", parseInt(e.target.value) || 0)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Carnet</label>
            <input data-testid="bike-field-license" value={form.license} onChange={(e) => set("license", e.target.value)} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Matrícula</label>
            <input data-testid="bike-field-matricula" value={form.matricula || ""} onChange={(e) => set("matricula", e.target.value.toUpperCase())} placeholder="1234ABC" className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Bastidor (6 últimos)</label>
            <input data-testid="bike-field-bastidor" value={form.bastidor || ""} maxLength={6} onChange={(e) => set("bastidor", e.target.value.toUpperCase())} placeholder="ABC123" className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Precio 1 día</label>
            <input data-testid="bike-field-p1" type="number" value={form.price_1d} onChange={(e) => set("price_1d", num(e.target.value))} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Precio 3 días</label>
            <input data-testid="bike-field-p3" type="number" value={form.price_3d} onChange={(e) => set("price_3d", num(e.target.value))} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Precio 7 días</label>
            <input data-testid="bike-field-p7" type="number" value={form.price_7d} onChange={(e) => set("price_7d", num(e.target.value))} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Fianza</label>
            <input data-testid="bike-field-deposit" type="number" value={form.deposit} onChange={(e) => set("deposit", num(e.target.value))} className="input-brutal mt-1" />
          </div>
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Unidades</label>
            <input data-testid="bike-field-stock" type="number" min={1} value={form.stock ?? 1} onChange={(e) => set("stock", Math.max(parseInt(e.target.value) || 1, 1))} className="input-brutal mt-1" />
            <p className="text-[10px] text-topacio-faded font-semibold mt-1.5">Cuántas motos iguales tenéis. Permite varias reservas del mismo modelo el mismo día.</p>
          </div>
          {(form.stock || 1) > 1 && (
            <div className="col-span-2 rounded-xl border-2 border-topacio-ink/15 bg-topacio-ink/[0.03] p-3">
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Unidades (matrícula + mote + bastidor)</label>
              <p className="text-[10px] text-topacio-faded font-semibold mt-1 mb-2">Identifica cada moto del mismo modelo. La matrícula y el mote se ven en el calendario; el bastidor sale en el contrato de esa unidad.</p>
              <div className="space-y-2">
                {Array.from({ length: form.stock }).map((_, i) => {
                  const u = form.units?.[i] || { plate: "", internal_name: "", bastidor: "", status: "ok" };
                  return (
                    <div key={i} className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] font-extrabold text-topacio-faded w-5 shrink-0">{i + 1}</span>
                      <input data-testid={`bike-unit-plate-${i}`} value={u.plate || ""} onChange={(e) => setUnit(i, "plate", e.target.value.toUpperCase())} placeholder="Matrícula (0106MRF)" className="input-brutal !py-2 !text-sm flex-1 min-w-[100px]" />
                      <input data-testid={`bike-unit-name-${i}`} value={u.internal_name || ""} onChange={(e) => setUnit(i, "internal_name", e.target.value)} placeholder="Mote (SR1 BLANCA)" className="input-brutal !py-2 !text-sm flex-1 min-w-[100px]" />
                      <input data-testid={`bike-unit-bastidor-${i}`} value={u.bastidor || ""} maxLength={6} onChange={(e) => setUnit(i, "bastidor", e.target.value.toUpperCase())} placeholder="Bastidor (6)" className="input-brutal !py-2 !text-sm w-[110px]" />
                      <select data-testid={`bike-unit-status-${i}`} value={u.status || "ok"} onChange={(e) => setUnit(i, "status", e.target.value)}
                        className={`text-[10px] font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-2 py-1.5 outline-none cursor-pointer ${(BIKE_STATUS[u.status || "ok"] || BIKE_STATUS.ok).cls}`}>
                        {UNIT_STATUS_OPTIONS.map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="col-span-2">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Foto de la moto</label>
            <div className="mt-1 flex items-center gap-3">
              {(photoPreview || form.image) && (
                <img data-testid="bike-photo-preview" src={photoPreview || form.image} alt="preview"
                  className="w-24 h-16 object-cover rounded-md border-2 border-topacio-ink bg-white shrink-0" />
              )}
              <label data-testid="bike-photo-upload-label" className="btn-secondary !px-4 !py-2.5 !text-xs cursor-pointer inline-flex items-center gap-2">
                <Plus className="w-4 h-4" /> {photoFile ? photoFile.name.slice(0, 22) : "Subir foto"}
                <input data-testid="bike-photo-input" type="file" accept="image/*" className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setPhotoFile(f);
                    if (photoPreview) URL.revokeObjectURL(photoPreview);
                    setPhotoPreview(f ? URL.createObjectURL(f) : null);
                  }} />
              </label>
            </div>
            <p className="text-[10px] text-topacio-faded font-semibold mt-1.5">Sube la foto directamente (JPG/PNG/WebP, máx. 10 MB) — se guarda en vuestro servidor. O pega una URL abajo.</p>
            <input data-testid="bike-field-image" value={form.image} onChange={(e) => set("image", e.target.value)} placeholder="URL de imagen (alternativa)" className="input-brutal mt-2" />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-topacio-ink">
            <input data-testid="bike-field-automatic" type="checkbox" checked={form.automatic} onChange={(e) => set("automatic", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
            Automática
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-topacio-ink">
            <input data-testid="bike-field-active" type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
            Visible en la web
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-topacio-ink col-span-2">
            <input data-testid="bike-field-coming-soon" type="checkbox" checked={!!form.coming_soon} onChange={(e) => set("coming_soon", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
            Próximamente (se ve en el catálogo, pero aún no se puede reservar)
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-topacio-ink col-span-2">
            <input data-testid="bike-field-topcase" type="checkbox" checked={!!form.topcase_included} onChange={(e) => set("topcase_included", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
            Baúl incluido en el precio (se anuncia en la web y no se cobra como extra)
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-topacio-ink col-span-2">
            <input data-testid="bike-field-keyless" type="checkbox" checked={!!form.keyless_included} onChange={(e) => set("keyless_included", e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" />
            Llave sin contacto / keyless (se anuncia en la ficha de la web)
          </label>
          <p className="col-span-2 text-[10px] text-topacio-faded font-semibold -mt-2">Las motos recién dadas de alta llevan la pegatina «Novedad» automáticamente durante 30 días.</p>
          {bike?.slug && <BikeHistory slug={bike.slug} headers={headers} />}
        </div>
        <button data-testid="bike-editor-save" disabled={busy} onClick={save} className="btn-primary w-full mt-6">
          Guardar
        </button>
      </div>
    </div>
  );
}

// ---- Línea de tiempo de taller de una moto (historial de estados) ----
function BikeHistory({ slug, headers }) {
  const [items, setItems] = useState(null);
  const [repairTotal, setRepairTotal] = useState(0);
  const [revenueTotal, setRevenueTotal] = useState(0);
  const [bookingsCount, setBookingsCount] = useState(0);
  const [maint, setMaint] = useState([]);
  const [lastKm, setLastKm] = useState(null);
  const [mForm, setMForm] = useState({ km: "", note: "", cost: "", date: "" });
  const [mBusy, setMBusy] = useState(false);
  const loadHistory = () => {
    if (!slug) return;
    axios.get(`${API}/admin/bikes/${slug}/history`, { headers })
      .then(({ data }) => {
        setItems(data.history);
        setRepairTotal(data.repair_total || 0);
        setRevenueTotal(data.revenue_total || 0);
        setBookingsCount(data.bookings_count || 0);
        setMaint(data.maintenance || []);
        setLastKm(data.last_km ?? null);
      })
      .catch(() => setItems([]));
  };
  useEffect(() => { loadHistory(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  const addMaint = async () => {
    if (mForm.km === "" && !mForm.note.trim() && mForm.cost === "") { toast.error("Indica km, nota o coste"); return; }
    setMBusy(true);
    try {
      await axios.post(`${API}/admin/bikes/${slug}/maintenance`, {
        km: mForm.km === "" ? null : parseInt(mForm.km) || 0,
        cost: mForm.cost === "" ? null : parseFloat(mForm.cost) || 0,
        note: mForm.note.trim(), date: mForm.date || "",
      }, { headers });
      toast.success("Mantenimiento registrado");
      setMForm({ km: "", note: "", cost: "", date: "" });
      loadHistory();
    } catch { toast.error("No se pudo guardar el mantenimiento"); } finally { setMBusy(false); }
  };
  if (!slug || !items) return null;
  const fmtWhen = (iso) => {
    try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  return (
    <div className="col-span-2 border-t-2 border-dashed border-zinc-200 pt-4" data-testid="bike-history">
      <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-3 flex items-center gap-1.5">
        <Wrench className="w-3.5 h-3.5" /> Historial de taller
      </p>
      {(bookingsCount > 0 || repairTotal > 0) && (
        <div data-testid="bike-roi-summary" className="flex flex-wrap gap-2 mb-3">
          <span className="badge-brutal bg-topacio-ink text-white !text-[10px]">{bookingsCount} alquileres</span>
          <span className="badge-brutal bg-topacio-green text-white !text-[10px]">Facturado: {revenueTotal}€</span>
          {repairTotal > 0 && <span className="badge-brutal bg-topacio-red text-white !text-[10px]">Taller: {repairTotal}€</span>}
          {bookingsCount > 0 && (
            <span className={`badge-brutal !text-[10px] ${revenueTotal - repairTotal >= 0 ? "bg-brand text-topacio-ink" : "bg-topacio-red text-white"}`}>
              Balance: {Math.round((revenueTotal - repairTotal) * 100) / 100}€
            </span>
          )}
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-xs text-topacio-faded font-semibold">Sin cambios de estado registrados todavía.</p>
      ) : (
        <div className="space-y-0 max-h-52 overflow-y-auto pr-1">
          {items.map((h, i) => {
            const meta = BIKE_STATUS[h.status] || BIKE_STATUS.ok;
            return (
              <div key={h.id || i} data-testid={`bike-history-${i}`} className="relative pl-5 pb-3 border-l-2 border-zinc-200 last:pb-0">
                <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full border-2 border-white ${meta.cls}`} />
                <div className="flex items-center justify-between gap-2">
                  <span className={`badge-brutal !text-[9px] ${meta.cls}`}>{meta.label}</span>
                  <span className="text-[10px] text-topacio-faded font-semibold shrink-0">{fmtWhen(h.at)}</span>
                </div>
                {h.note && <p className="text-[11px] text-topacio-ink font-semibold mt-1">{h.note}</p>}
                <p className="text-[10px] text-topacio-faded font-semibold mt-0.5">
                  {h.by || "sistema"}{h.repair_cost != null ? ` · coste est. ${h.repair_cost}€` : ""}
                </p>
              </div>
            );
          })}
        </div>
      )}
      {/* Mantenimiento / km por moto (y por matrícula) */}
      <div className="mt-5 border-t-2 border-dashed border-zinc-200 pt-4" data-testid="bike-maintenance">
        <p className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded mb-3 flex items-center gap-1.5">
          <Gauge className="w-3.5 h-3.5" /> Mantenimiento y kilómetros
          {lastKm != null && <span className="badge-brutal bg-topacio-ink text-white !text-[9px]">Último km: {lastKm.toLocaleString("es-ES")}</span>}
        </p>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <div className="w-24">
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Km</label>
            <input data-testid="maint-km-input" type="number" min="0" value={mForm.km} onChange={(e) => setMForm({ ...mForm, km: e.target.value })} className="input-brutal !py-2 !text-sm mt-1" />
          </div>
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Nota (cambio aceite, neumáticos…)</label>
            <input data-testid="maint-note-input" value={mForm.note} onChange={(e) => setMForm({ ...mForm, note: e.target.value })} className="input-brutal !py-2 !text-sm mt-1" />
          </div>
          <div className="w-24">
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Coste €</label>
            <input data-testid="maint-cost-input" type="number" min="0" step="0.01" value={mForm.cost} onChange={(e) => setMForm({ ...mForm, cost: e.target.value })} className="input-brutal !py-2 !text-sm mt-1" />
          </div>
          <div className="w-36">
            <label className="text-[10px] font-extrabold uppercase tracking-wider text-topacio-faded">Fecha</label>
            <input data-testid="maint-date-input" type="date" value={mForm.date} onChange={(e) => setMForm({ ...mForm, date: e.target.value })} className="input-brutal !py-2 !text-sm mt-1" />
          </div>
          <button type="button" data-testid="maint-add-btn" onClick={addMaint} disabled={mBusy} className="btn-primary !px-4 !py-2 !text-xs">
            {mBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Registrar
          </button>
        </div>
        {maint.length === 0 ? (
          <p className="text-xs text-topacio-faded font-semibold">Sin entradas de mantenimiento todavía.</p>
        ) : (
          <div className="space-y-1.5 max-h-44 overflow-y-auto pr-1">
            {maint.map((m, i) => (
              <div key={m.id || i} data-testid={`maint-row-${i}`} className="flex items-center justify-between gap-2 bg-topacio-sand/50 rounded-lg px-3 py-1.5">
                <div className="min-w-0">
                  <span className="text-[11px] font-extrabold text-topacio-ink">
                    {m.km != null ? `${Number(m.km).toLocaleString("es-ES")} km` : "—"}
                    {m.plate ? ` · ${m.plate}` : ""}
                  </span>
                  {m.note && <span className="text-[11px] text-topacio-faded font-semibold"> · {m.note}</span>}
                </div>
                <span className="text-[10px] text-topacio-faded font-semibold shrink-0">
                  {m.cost != null ? `${m.cost}€ · ` : ""}{m.date || (m.at || "").slice(0, 10)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Componentes del NOC (centro de operaciones) ----
function NocKpi({ label, value, sub, tone, testid, icon: Icon }) {
  return (
    <div data-testid={testid} className="bg-white/5 border-2 border-white/15 rounded-xl p-5 hover:border-brand/60 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-zinc-400">{label}</span>
        <Icon className={`w-4 h-4 ${tone}`} />
      </div>
      <div className="font-display font-black text-2xl sm:text-3xl text-white mt-3">{value}</div>
      {sub && <div className="text-xs text-zinc-500 font-semibold mt-1">{sub}</div>}
    </div>
  );
}

function NocCard({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid} className="bg-white/5 border-2 border-white/15 rounded-xl p-5">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-extrabold text-zinc-400 mb-4">
        {Icon && <Icon className="w-3.5 h-3.5 text-brand" />} {title}
      </div>
      {children}
    </div>
  );
}

function NocBars({ data, valueKey, labelKey, money = false, testid, every = 5 }) {
  const max = Math.max(...data.map((d) => d[valueKey]), 1);
  return (
    <div data-testid={testid} className="flex items-end gap-[3px] h-32">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1.5 h-full group"
          title={`${d[labelKey]}: ${money ? `${d[valueKey]}€` : d[valueKey]}`}>
          <div className={`w-full rounded-sm border border-black/60 transition-colors ${d[valueKey] > 0 ? "bg-brand group-hover:bg-white" : "bg-white/10"}`}
            style={{ height: `${d[valueKey] > 0 ? Math.max((d[valueKey] / max) * 100, 7) : 2}%` }} />
          <span className="text-[8px] text-zinc-600 font-mono h-2">{i % every === 0 ? d[labelKey] : ""}</span>
        </div>
      ))}
    </div>
  );
}

// ---- Canales de reserva: online / partners / tienda ----
const CHANNEL_META = {
  online: { label: "Web online", color: "#2A9D8F", icon: Globe },
  partner: { label: "Partners", color: "#FFCC00", icon: Handshake },
  tienda: { label: "Tienda / publicidad", color: "#E63946", icon: Store },
};



function HealthHistoryMini({ history }) {
  if (!history || !history.points?.length) {
    return <p className="text-[10px] text-zinc-500 font-semibold border-t border-white/10 pt-3">Recolectando historial del watchdog…</p>;
  }
  return (
    <div data-testid="server-health-history" className="border-t border-white/10 pt-3">
      <div className="flex items-center justify-between text-[10px] font-extrabold uppercase tracking-wider text-zinc-500 mb-2">
        <span>Historial 24 h</span>
        <span className={history.uptime >= 99 ? "text-topacio-green" : "text-topacio-red"}>{history.uptime ?? "—"}% online</span>
      </div>
      <div className="flex items-end gap-[2px] h-10">
        {history.points.map((p, i) => (
          <div key={p.at || i} title={`${p.at?.slice(11, 16)} · disco ${p.disk ?? "—"}%${p.status !== "ok" ? " · incidencia" : ""}`}
            className={`flex-1 rounded-sm ${p.status === "ok" ? "bg-topacio-green/80" : p.status === "warning" ? "bg-brand" : "bg-topacio-red"}`}
            style={{ height: `${Math.max(18, Math.min(100, p.disk ?? 20))}%` }} />
        ))}
      </div>
      <p className="text-[9px] text-zinc-500 font-semibold mt-2">
        {history.readings} lecturas · {history.incidents} incidencias · último backup hace {history.backup_age_hours ?? "—"} h
      </p>
    </div>
  );
}

function NocChannels({ channels }) {
  const rows = Object.entries(CHANNEL_META).map(([key, meta]) => ({
    key, ...meta, count: channels?.[key]?.count || 0, revenue: channels?.[key]?.revenue || 0,
  }));
  const totalCount = rows.reduce((s, r) => s + r.count, 0);
  const totalRevenue = rows.reduce((s, r) => s + r.revenue, 0);
  const pct = (v, t) => (t > 0 ? Math.round((v / t) * 100) : 0);
  return (
    <>
      <div className="flex h-5 rounded-full overflow-hidden border-2 border-white/20 mb-1.5" data-testid="noc-channels-bar">
        {rows.filter((r) => r.revenue > 0).map((r) => (
          <div key={r.key} title={`${r.label}: ${pct(r.revenue, totalRevenue)}% de la facturación`}
            style={{ width: `${(r.revenue / Math.max(totalRevenue, 1)) * 100}%`, background: r.color }} />
        ))}
      </div>
      <p className="text-[9px] text-zinc-600 font-mono mb-4">barra: % de facturación por canal</p>
      {totalCount === 0 ? (
        <p className="text-xs text-zinc-500 font-semibold">Sin reservas con ingresos todavía.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.key} data-testid={`noc-channel-${r.key}`} className="flex items-center gap-3">
              <r.icon className="w-4 h-4 shrink-0" style={{ color: r.color }} />
              <div className="flex-1">
                <div className="flex justify-between text-xs font-bold text-zinc-300 mb-1">
                  <span>{r.label}</span>
                  <span className="text-white">{r.count} res. · {r.revenue}€</span>
                </div>
                <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full transition-all duration-700" style={{ width: `${pct(r.count, totalCount)}%`, background: r.color }} />
                </div>
                <div className="text-[10px] text-zinc-500 font-semibold mt-0.5">
                  {pct(r.count, totalCount)}% de las reservas · {pct(r.revenue, totalRevenue)}% de la facturación
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---- Edición completa de usuario staff ----
function UserEditModal({ user, headers, isSuper, selfEmail, onClose, onSaved }) {
  const isSelf = user.email === selfEmail;
  const isSuperTarget = user.role === "superadmin";
  // superadmin cambia cualquier rol; admin solo entre los roles básicos (consulta/vendedor/entrega)
  const canChangeRole = !isSelf && !isSuperTarget && (isSuper || ["viewer", "vendedor", "entrega", "mecanico", "lavamotos"].includes(user.role));
  const [form, setForm] = useState({ name: user.name || "", role: user.role, password: "", active: user.active !== false });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: form.name };
      if (canChangeRole) payload.role = form.role;
      if (!isSuperTarget && !isSelf) payload.active = form.active;
      if (form.password) payload.password = form.password;
      await axios.patch(`${API}/admin/users/${encodeURIComponent(user.email)}`, payload, { headers });
      toast.success("Usuario actualizado");
      onSaved();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "password_too_short" ? "La contraseña debe tener mínimo 8 caracteres"
        : d === "superadmin_only" ? "Solo el superadministrador puede hacer eso"
        : d === "cannot_modify_superadmin" ? "El rol y el estado del superadministrador no se pueden tocar"
        : "Error al guardar");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="user-editor-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2"><UserCog className="w-5 h-5 text-topacio-red" /> Editar usuario</h3>
          <button data-testid="user-editor-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs font-mono text-topacio-faded mb-4">{user.email}</p>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nombre</label>
            <input data-testid="user-edit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-brutal mt-1" />
          </div>
          {canChangeRole && (
            <div>
              <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Rol</label>
              <select data-testid="user-edit-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-brutal mt-1">
                <option value="viewer">Consulta (solo lectura)</option>
                <option value="vendedor">Vendedor (reservas de tienda)</option>
                <option value="entrega">Entrega (parte + firma móvil)</option>
                <option value="mecanico">Mecánico (marca «revisada»)</option>
                <option value="lavamotos">Lavamotos (marca «OK»)</option>
                {isSuper && <option value="admin">Administrador</option>}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Nueva contraseña</label>
            <input data-testid="user-edit-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Dejar vacío para no cambiarla" className="input-brutal mt-1" />
          </div>
          {!isSuperTarget && !isSelf && (
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input data-testid="user-edit-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="w-5 h-5 accent-[#FFCC00]" />
              <span className="text-sm font-bold text-topacio-ink">Acceso activo <span className="text-topacio-faded font-semibold">(desmárcalo para bloquear su entrada sin borrarlo)</span></span>
            </label>
          )}
          <button data-testid="user-editor-save" onClick={save} disabled={busy} className="btn-primary w-full !py-3">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Cambio de estado de moto con nota/coste (sin prompts nativos) ----
function BikeStatusModal({ change, setChange, busy, onClose, onSave }) {
  const meta = BIKE_STATUS[change.status] || { label: change.status };
  const needsCost = ["por_reparar", "presupuesto", "pendiente_pieza"].includes(change.status);
  return (
    <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="bike-status-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative card-brutal w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2">
            <Bike className="w-5 h-5 text-topacio-red" /> {change.name}
          </h3>
          <button data-testid="bike-status-cancel" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
        </div>
        <span className={`badge-brutal ${meta.cls} mb-4 inline-flex`}>{meta.label}</span>
        <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nota interna</label>
        <textarea data-testid="bike-status-note-input" value={change.note} rows={3}
          onChange={(e) => setChange({ ...change, note: e.target.value })}
          placeholder="Motivo, pieza, detalle para el equipo…" className="input-brutal mt-1 resize-none" />
        {needsCost && (
          <div className="mt-4">
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Coste estimado de reparación (€)</label>
            <input data-testid="bike-status-cost-input" type="number" min="0" step="0.01" value={change.cost}
              onChange={(e) => setChange({ ...change, cost: e.target.value })} placeholder="Opcional" className="input-brutal mt-1" />
          </div>
        )}
        <button data-testid="bike-status-save" onClick={onSave} disabled={busy} className="btn-primary w-full mt-5">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar estado
        </button>
      </div>
    </div>
  );
}


export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const r = localStorage.getItem("admin_role");
    if (r === "vendedor") return "bookings";
    if (r === "mecanico" || r === "lavamotos") return "fleet";
    return "noc";
  });
  const [noc, setNoc] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [fleetSort, setFleetSort] = useState("pizarra");
  const [coupons, setCoupons] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [newCoupon, setNewCoupon] = useState({ code: "", type: "percent", value: 10 });
  const [reviewStats, setReviewStats] = useState(null);
  const [season, setSeason] = useState("verano");
  const [genBusy, setGenBusy] = useState(false);
  const [googleKey, setGoogleKey] = useState({ configured: false });
  const [googleKeyInput, setGoogleKeyInput] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "viewer" });
  const [groups, setGroups] = useState([]);
  const [groupForm, setGroupForm] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [commissionFor, setCommissionFor] = useState(null);
  const [depositFor, setDepositFor] = useState(null);
  const [detailRef, setDetailRef] = useState(null);
  // filtros de la pestaña Reservas (buscador, estado, ocultar completadas, impresión por mes)
  const [bkSearch, setBkSearch] = useState("");
  const [bkStatusFilter, setBkStatusFilter] = useState("all");
  const [hideCompleted, setHideCompleted] = useState(false);
  const [hideCancelled, setHideCancelled] = useState(false);
  const [printMonth, setPrintMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [editingUser, setEditingUser] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [statusChange, setStatusChange] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [serverHealth, setServerHealth] = useState(null);
  const [healthHistory, setHealthHistory] = useState(null);
  const [bookingOptions, setBookingOptions] = useState(null);
  const [gaInput, setGaInput] = useState("");
  const [gaSaving, setGaSaving] = useState(false);
  const [sesInfo, setSesInfo] = useState(null);
  const [sesDate, setSesDate] = useState("");
  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };
  const role = localStorage.getItem("admin_role") || "admin";
  const isVendor = role === "vendedor";
  const isMecanico = role === "mecanico";
  const isLavamotos = role === "lavamotos";
  const isTaller = isMecanico || isLavamotos;
  const tallerStatus = isMecanico ? "revisada" : "ok";
  const canEdit = role === "admin" || role === "superadmin";
  const isSuper = role === "superadmin";

  const load = async () => {
    try {
      if (isTaller) {
        // mecánico/lavamotos: solo flota (para el cambio rápido de estado) y calendario (vista propia)
        const bk = await axios.get(`${API}/admin/bikes`, { headers });
        setBikes(bk.data.bikes);
        return;
      }
      if (isVendor) {
        // vendedor: solo planificación de reservas (sanitizada) y flota para el alta manual
        const [b, bk] = await Promise.all([
          axios.get(`${API}/admin/bookings`, { headers }),
          axios.get(`${API}/admin/bikes`, { headers }),
        ]);
        setBookings(b.data.bookings);
        setBikes(bk.data.bikes);
        return;
      }
      const [b, s, bk, cp, ph] = await Promise.all([
        axios.get(`${API}/admin/bookings`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/bikes`, { headers }),
        axios.get(`${API}/admin/coupons`, { headers }),
        axios.get(`${API}/admin/gallery`, { headers }),
      ]);
      setBookings(b.data.bookings);
      setStats(s.data);
      setBikes(bk.data.bikes);
      setCoupons(cp.data.coupons);
      setPhotos(ph.data.photos);
      axios.get(`${API}/admin/reviews/stats`, { headers }).then(({ data }) => setReviewStats(data)).catch(() => {});
      axios.get(`${API}/admin/settings/google-key`, { headers }).then(({ data }) => setGoogleKey(data)).catch(() => {});
      if (role !== "viewer") axios.get(`${API}/admin/users`, { headers }).then(({ data }) => setStaffUsers(data.users)).catch(() => {});
      axios.get(`${API}/groups`).then(({ data }) => setGroups(data.groups)).catch(() => {});
      axios.get(`${API}/admin/noc`, { headers }).then(({ data }) => setNoc(data)).catch(() => {});
      axios.get(`${API}/admin/server-health`, { headers }).then(({ data }) => setServerHealth(data)).catch(() => {});
      axios.get(`${API}/admin/server-health/history?hours=24`, { headers }).then(({ data }) => setHealthHistory(data)).catch(() => {});
      axios.get(`${API}/admin/settings/booking-options`, { headers }).then(({ data }) => { setBookingOptions(data); setGaInput(data.ga_id || ""); }).catch(() => {});
      axios.get(`${API}/admin/ses-hospedajes/preview`, { headers }).then(({ data }) => setSesInfo(data)).catch(() => {});
    } catch {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    }
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    if (role === "entrega") {
      navigate("/entregas");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timbre: avisa (sonido + toast) cuando entra una reserva nueva por la web
  const lastOnlineRef = useRef(null);
  const playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      if (ctx.state === "suspended") ctx.resume();
      const beep = (freq, start, dur) => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination); o.type = "sine"; o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur);
      };
      beep(880, 0, 0.35); beep(1320, 0.18, 0.4); // dos notas tipo "ding-dong"
      setTimeout(() => ctx.close(), 1200);
    } catch { /* el navegador puede bloquear el audio sin interacción previa */ }
  };

  useEffect(() => {
    if (role === "entrega" || role === "viewer" || isTaller) return undefined;
    const channelOf = (b) => b.channel || (b.partner_code ? "partner" : "online");
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/bookings`, { headers });
        const online = (data.bookings || []).filter((b) => channelOf(b) === "online");
        const newest = online[0]; // el endpoint ordena por created_at desc
        if (!newest) return;
        if (lastOnlineRef.current === null) { lastOnlineRef.current = newest.ref; return; }
        if (newest.ref !== lastOnlineRef.current) {
          lastOnlineRef.current = newest.ref;
          playChime();
          toast.success(`🔔 Nueva reserva web: ${newest.name || newest.ref} · ${newest.bike_name || ""}`, { duration: 10000 });
          if (!isVendor) load();
        }
      } catch { /* silencioso: es un sondeo de fondo */ }
    };
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Aviso en panel para mecánico/lavamotos: motos pendientes de su tarea ---
  const tallerPendingStatuses = isMecanico ? ["por_revisar"] : isLavamotos ? ["pendiente_limpiar", "en_lavadero"] : [];
  const pendingBikes = isTaller ? bikes.filter((b) => tallerPendingStatuses.includes(b.status || "ok")) : [];
  const tallerSeenRef = useRef(null);
  useEffect(() => {
    if (!isTaller) return undefined;
    const statuses = isMecanico ? ["por_revisar"] : ["pendiente_limpiar", "en_lavadero"];
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/bikes`, { headers });
        const pend = (data.bikes || []).filter((b) => statuses.includes(b.status || "ok"));
        const seen = tallerSeenRef.current;
        if (seen === null) {
          tallerSeenRef.current = new Set(pend.map((b) => b.slug));
        } else {
          const fresh = pend.filter((b) => !seen.has(b.slug));
          if (fresh.length) {
            playChime();
            const label = isMecanico ? "por revisar" : "pendiente de limpiar";
            toast.success(`🔔 ${fresh.length} moto(s) ${label}: ${fresh.map((b) => b.name).join(", ")}`, { duration: 10000 });
          }
          tallerSeenRef.current = new Set(pend.map((b) => b.slug));
        }
        setBikes(data.bikes);
      } catch { /* sondeo de fondo */ }
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (ref, status) => {
    await axios.patch(`${API}/admin/bookings/${ref}`, { status }, { headers });
    toast.success("Estado actualizado");
    load();
  };

  // --- Filtro de reservas: buscador (nombre/moto/ref/teléfono/email) + estado + ocultar completadas ---
  const filteredBookings = bookings.filter((bk) => {
    if (hideCompleted && bk.status === "completed") return false;
    if (hideCancelled && bk.status === "cancelled") return false;
    if (bkStatusFilter !== "all" && bk.status !== bkStatusFilter) return false;
    const q = bkSearch.trim().toLowerCase();
    if (!q) return true;
    return [bk.ref, bk.name, bk.bike_name, bk.phone, bk.email, bk.partner_code]
      .some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  // --- Imprimir las reservas de un mes (YYYY-MM) en una ventana limpia ---
  const printBookingsMonth = () => {
    const rows = bookings
      .filter((b) => (b.start_date || "").slice(0, 7) === printMonth && b.status !== "cancelled")
      .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
    const [y, m] = printMonth.split("-");
    const title = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("es-ES", { month: "long", year: "numeric" });
    const esc = (s) => (s == null ? "" : String(s)).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const chLabel = (b) => (b.channel || (b.partner_code ? "partner" : "online")) === "tienda" ? "Tienda" : (b.channel || (b.partner_code ? "partner" : "online")) === "partner" ? `Partner ${b.partner_code || ""}` : "Web";
    const body = rows.map((b) => `<tr>
      <td>${esc(b.ref)}</td><td>${esc(b.name)}<br><small>${esc(b.phone || "")}</small></td>
      <td>${esc(b.bike_name)}</td><td>${esc(b.start_date)} ${esc(b.pickup_time || "")}<br>→ ${esc(b.end_date)} ${esc(b.return_time || "")}</td>
      <td style="text-align:center">${esc(b.days)}</td><td style="text-align:right">${esc(b.total)}€</td>
      <td>${esc(chLabel(b))}</td><td>${esc((STATUS_LABELS[b.status] || b.status))}</td></tr>`).join("");
    const totalRev = rows.reduce((s, b) => s + (Number(b.total) || 0), 0);
    const w = window.open("", "_blank");
    if (!w) { toast.error("Permite las ventanas emergentes para imprimir"); return; }
    w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Reservas ${title}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:#111;margin:28px}
      h1{font-size:20px;margin:0 0 2px}p.sub{color:#666;margin:0 0 18px;font-size:13px}
      table{width:100%;border-collapse:collapse;font-size:12px}
      th{background:#111;color:#fff;text-align:left;padding:7px 8px;text-transform:uppercase;font-size:10px;letter-spacing:.5px}
      td{border-bottom:1px solid #ddd;padding:6px 8px;vertical-align:top}
      small{color:#888}tfoot td{font-weight:bold;border-top:2px solid #111}
      @media print{@page{margin:14mm}}</style></head><body>
      <h1>Rent a Moto Topacio · Reservas de ${title}</h1>
      <p class="sub">${rows.length} reserva(s) · generado ${new Date().toLocaleString("es-ES")}</p>
      <table><thead><tr><th>Ref</th><th>Cliente</th><th>Moto</th><th>Fechas</th><th>Días</th><th>Total</th><th>Canal</th><th>Estado</th></tr></thead>
      <tbody>${body || '<tr><td colspan="8" style="text-align:center;padding:24px;color:#888">Sin reservas este mes.</td></tr>'}</tbody>
      <tfoot><tr><td colspan="5">Total facturación</td><td style="text-align:right">${totalRev}€</td><td colspan="2"></td></tr></tfoot>
      </table></body></html>`);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 350);
  };

  // Tablero de flota: cambio de estado operativo con nota y coste de reparación opcionales
  const patchBikeStatus = async (slug, payload) => {
    try {
      await axios.patch(`${API}/admin/bikes/${slug}/status`, payload, { headers });
      toast.success(`${(BIKE_STATUS[payload.status] || {}).label || payload.status} · estado actualizado`);
      load();
      return true;
    } catch {
      toast.error("No se pudo cambiar el estado");
      return false;
    }
  };

  const requestBikeStatus = (slug, status) => {
    if (status === "ok") {
      patchBikeStatus(slug, { status, note: "", repair_cost: null });
      return;
    }
    const bk = bikes.find((b) => b.slug === slug);
    setStatusChange({ slug, status, name: bk?.name || slug, note: "", cost: "" });
  };

  const confirmBikeStatus = async () => {
    if (!statusChange) return;
    setStatusBusy(true);
    const ok = await patchBikeStatus(statusChange.slug, {
      status: statusChange.status,
      note: statusChange.note,
      repair_cost: statusChange.cost ? parseFloat(statusChange.cost) || null : null,
    });
    setStatusBusy(false);
    if (ok) setStatusChange(null);
  };

  const resendPrecheckin = async (ref) => {
    try {
      const { data } = await axios.post(`${API}/admin/bookings/${ref}/precheckin/resend`, {}, { headers });
      toast.success(data.already_completed ? "La documentación ya estaba en la ficha" : "Email de precheck-in reenviado");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d === "email_required" ? "La reserva no tiene email" : d === "email_failed" ? "No se pudo enviar el email" : "Error al reenviar");
    }
  };

  const toggleDeliveryPickup = async () => {
    if (!bookingOptions) return;
    const next = !bookingOptions.delivery_pickup_enabled;
    try {
      const { data } = await axios.patch(`${API}/admin/settings/booking-options`, { delivery_pickup_enabled: next }, { headers });
      setBookingOptions(data);
      toast.success(next ? "Entregas fuera de oficina activadas" : "Recogida solo en oficina");
    } catch {
      toast.error("No se pudo cambiar la opción");
    }
  };

  const toggleMaintenanceMode = async () => {
    if (!bookingOptions) return;
    const next = !bookingOptions.maintenance_mode;
    try {
      const { data } = await axios.patch(`${API}/admin/settings/booking-options`, { maintenance_mode: next }, { headers });
      setBookingOptions(data);
      toast.success(next ? "Web pública en el taller" : "Web pública reactivada");
    } catch {
      toast.error("No se pudo cambiar el modo mantenimiento");
    }
  };

  const loadSesPreview = async (date) => {
    try {
      const { data } = await axios.get(`${API}/admin/ses-hospedajes/preview${date ? `?date=${date}` : ""}`, { headers });
      setSesInfo(data);
    } catch { toast.error("No se pudo cargar el resumen SES"); }
  };

  const downloadSesXml = async () => {
    try {
      const day = sesDate || sesInfo?.date || "";
      const res = await axios.get(`${API}/admin/ses-hospedajes.xml${day ? `?date=${day}` : ""}`, { headers, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SES_HOSPEDAJES_${day}.xml`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fichero SES descargado: súbelo a la sede del Ministerio");
    } catch { toast.error("No se pudo generar el XML"); }
  };

  const changeReviewAlertDays = async (delta) => {
    if (!bookingOptions) return;
    const next = Math.min(90, Math.max(1, (bookingOptions.review_alert_days || 7) + delta));
    try {
      const { data } = await axios.patch(`${API}/admin/settings/booking-options`, { review_alert_days: next }, { headers });
      setBookingOptions(data);
      toast.success(`Alerta de revisión: ${next} días`);
    } catch {
      toast.error("No se pudo cambiar el umbral");
    }
  };

  const saveGaId = async () => {
    setGaSaving(true);
    try {
      const { data } = await axios.patch(`${API}/admin/settings/booking-options`, { ga_id: gaInput.trim() }, { headers });
      setBookingOptions(data);
      setGaInput(data.ga_id || "");
      window.__GA_ID = data.ga_id || "";
      toast.success(data.ga_id ? "Google Analytics guardado. Se activará con el consentimiento de cookies." : "Google Analytics desactivado");
    } catch (e) {
      toast.error(e.response?.data?.detail === "bad_ga_id" ? "ID no válido. Debe ser como G-XXXXXXXXXX" : "No se pudo guardar");
    } finally { setGaSaving(false); }
  };

  const changeReturnReminderLead = async (delta) => {
    if (!bookingOptions) return;
    const next = Math.min(360, Math.max(15, (bookingOptions.return_reminder_lead_min || 120) + delta));
    try {
      const { data } = await axios.patch(`${API}/admin/settings/booking-options`, { return_reminder_lead_min: next }, { headers });
      setBookingOptions(data);
      toast.success(`Recordatorio de devolución: ${next} min antes`);
    } catch {
      toast.error("No se pudo cambiar la antelación");
    }
  };

  const unmarkCommissionPaid = async (ref) => {
    await axios.patch(`${API}/admin/bookings/${ref}/commission`, { paid: false }, { headers });
    toast.success("Comisión devuelta a pendiente");
    load();
  };

  const saveManualBooking = async (form) => {
    await axios.post(`${API}/admin/bookings/manual`, form, { headers });
    toast.success("Reserva de tienda registrada y fechas bloqueadas");
    load();
  };

  const saveBike = async (form, photoFile) => {
    let slug = editing?.slug;
    if (editing) {
      await axios.put(`${API}/admin/bikes/${editing.slug}`, form, { headers });
      toast.success("Moto actualizada");
    } else {
      const { data } = await axios.post(`${API}/admin/bikes`, form, { headers });
      slug = data.bike.slug;
      toast.success("Moto creada");
    }
    if (photoFile && slug) {
      const fd = new FormData();
      fd.append("file", photoFile);
      try {
        await axios.post(`${API}/admin/bikes/${slug}/photo`, fd, { headers });
        toast.success("Foto de la moto subida");
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(d === "too_big" ? "Foto demasiado grande (máx. 10 MB)" : "La moto se guardó pero la foto falló");
      }
    }
    load();
  };

  const deleteBike = async (slug) => {
    if (!window.confirm("⚠️ Vas a ELIMINAR esta moto DEFINITIVAMENTE (desaparecerá de la flota y de la pizarra).\n\nSi solo quieres que no salga en la web, cancela y usa 'ocultar'.\n\n¿Eliminar del todo?")) return;
    try {
      await axios.delete(`${API}/admin/bikes/${slug}?hard=true`, { headers });
      toast.success("Moto eliminada definitivamente");
      load();
    } catch (e) {
      if (e.response?.data?.detail === "has_bookings")
        toast.error("No se puede eliminar: tiene reservas activas o futuras. Reasígnalas o espera a que terminen.");
      else toast.error("No se pudo eliminar la moto");
    }
  };

  // Reordenar la flota según la pizarra física: mueve una moto arriba/abajo y guarda el orden
  const moveBike = async (slug, dir) => {
    const ordered = [...bikes].sort((a, b) => {
      const ao = typeof a.sort_order === "number" ? a.sort_order : 10000;
      const bo = typeof b.sort_order === "number" ? b.sort_order : 10000;
      return ao - bo;
    });
    const i = ordered.findIndex((b) => b.slug === slug);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ordered.length) return;
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
    const slugs = ordered.map((b) => b.slug);
    setBikes(ordered.map((b, idx) => ({ ...b, sort_order: idx })));
    try {
      await axios.put(`${API}/admin/bikes/order`, { slugs }, { headers });
    } catch {
      toast.error("No se pudo guardar el orden");
      load();
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code.trim()) return;
    try {
      await axios.post(`${API}/admin/coupons`, newCoupon, { headers });
      toast.success(`Cupón ${newCoupon.code.toUpperCase()} creado`);
      setNewCoupon({ code: "", type: "percent", value: 10 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "Coupon exists" ? "Ese código ya existe" : "Error al crear");
    }
  };

  const toggleCoupon = async (c) => {
    await axios.patch(`${API}/admin/coupons/${c.code}`, { code: c.code, type: c.type, value: c.value, active: !c.active }, { headers });
    load();
  };

  const deleteCoupon = async (code) => {
    await axios.delete(`${API}/admin/coupons/${code}`, { headers });
    toast.success("Cupón eliminado");
    load();
  };

  const setPhotoStatus = async (id, status) => {
    await axios.patch(`${API}/admin/gallery/${id}`, { status }, { headers });
    toast.success(status === "approved" ? "Foto publicada" : "Foto rechazada");
    load();
  };

  const regenerateDemo = async () => {
    setGenBusy(true);
    try {
      await axios.post(`${API}/admin/gallery/demo-refresh`, { season }, { headers });
      toast.success("Generando fotos nuevas con IA… tardan 1-2 minutos");
      for (let i = 0; i < 40; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        try {
          const { data } = await axios.get(`${API}/admin/reviews/stats`, { headers });
          setReviewStats(data);
          if (data.gen?.status === "done") {
            toast.success("¡Galería de temporada lista! Ya está visible en la web");
            load();
            break;
          }
          if (data.gen?.status === "error") {
            toast.error(`Error al generar: ${data.gen?.error || "desconocido"}`);
            break;
          }
        } catch { break; }
      }
    } catch (e) {
      toast.error(e.response?.status === 409 ? "Ya hay una generación en curso" : "Error al iniciar la generación");
    } finally {
      setGenBusy(false);
    }
  };

  const saveGoogleKey = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/settings/google-key`, { api_key: googleKeyInput }, { headers });
      toast.success(data.saved ? "Clave de Google guardada. Las reseñas reales aparecerán en la web en segundos." : "Clave eliminada");
      setGoogleKeyInput("");
      load();
    } catch {
      toast.error("No se pudo guardar la clave");
    }
  };

  const createStaff = async () => {
    try {
      await axios.post(`${API}/admin/users`, newUser, { headers });
      toast.success(`Usuario ${(ROLE_LABELS[newUser.role] || newUser.role).toLowerCase()} creado`);
      setNewUser({ name: "", email: "", password: "", role: "viewer" });
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "email_exists") toast.error("Ese email ya existe");
      else if (d === "password_too_short") toast.error("La contraseña debe tener al menos 8 caracteres");
      else if (d === "superadmin_only") toast.error("Solo el superadministrador puede crear administradores");
      else toast.error("No se pudo crear el usuario");
    }
  };

  const deleteStaff = async (email) => {
    if (!window.confirm(`¿Eliminar el acceso de ${email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${email}`, { headers });
      toast.success("Usuario eliminado");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "superadmin_only" ? "Solo el superadministrador puede eliminar usuarios" : "No se pudo eliminar");
    }
  };

  const saveGroup = async () => {
    try {
      const payload = {
        license: groupForm.license,
        price_1d: parseFloat(groupForm.price_1d) || 0,
        price_3d: parseFloat(groupForm.price_3d) || 0,
        price_7d: parseFloat(groupForm.price_7d) || 0,
        deposit: parseFloat(groupForm.deposit) || 0,
        insurance_per_day: parseFloat(groupForm.insurance_per_day) || 0,
        franchise: parseFloat(groupForm.franchise) || 0,
      };
      if (groupForm.isNew) {
        await axios.post(`${API}/admin/groups`, { ...payload, letter: groupForm.letter }, { headers });
        toast.success(`Grupo ${groupForm.letter.toUpperCase()} creado`);
      } else {
        const { data } = await axios.put(`${API}/admin/groups/${groupForm.letter}`,
          { ...payload, apply_to_bikes: !!groupForm.apply }, { headers });
        toast.success(data.bikes_updated > 0
          ? `Grupo ${groupForm.letter} actualizado y aplicado a ${data.bikes_updated} motos`
          : `Grupo ${groupForm.letter} actualizado`);
      }
      setGroupForm(null);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "group_exists") toast.error("Esa letra ya existe");
      else toast.error("No se pudo guardar el grupo");
    }
  };

  const deleteGroup = async (letter) => {
    if (!window.confirm(`¿Eliminar el grupo ${letter}?`)) return;
    try {
      await axios.delete(`${API}/admin/groups/${letter}`, { headers });
      toast.success(`Grupo ${letter} eliminado`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail === "group_in_use" ? "Hay motos en ese grupo: reasígnalas antes" : "No se pudo eliminar");
    }
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const TABS = isVendor
    ? [["bookings", "Reservas"]]
    : isTaller
    ? [["calendar", "Calendario"], ["fleet", "Flota"]]
    : [["noc", "NOC"], ["bookings", "Reservas"], ["calendar", "Calendario"], ["customers", "Clientes"], ["partners", "Partners"], ["fleet", "Flota"], ["groups", "Tarifas"], ["coupons", "Cupones"], ["photos", `Fotos${photos.filter((p) => p.status === "pending").length ? ` (${photos.filter((p) => p.status === "pending").length})` : ""}`], ["reviews", "Valoraciones"], ["content", "Contenido"], ...(canEdit ? [["users", "Usuarios"]] : []), ...(role === "superadmin" ? [["logs", "Registros"]] : [])];

  return (
    <div className="min-h-screen bg-topacio-sand" data-testid="admin-dashboard">
      <header className="border-b-2 border-topacio-ink bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-topacio.jpg" alt="Topacio" className="h-9 w-auto rounded border-2 border-topacio-ink" />
            <span className="font-display font-bold text-sm text-topacio-ink">PANEL</span>
          </div>
          <div className="flex items-center gap-3">
            {isTaller && (
              <button data-testid="taller-bell" onClick={() => setTab("fleet")} title={pendingBikes.length ? `${pendingBikes.length} moto(s) pendiente(s)` : "Sin motos pendientes"}
                className="relative flex items-center text-topacio-faded hover:text-topacio-ink transition-colors">
                <Bell className="w-5 h-5" />
                {pendingBikes.length > 0 && (
                  <span data-testid="taller-bell-count" className="absolute -top-2 -right-2 min-w-[18px] h-[18px] px-1 rounded-full bg-topacio-red text-white text-[10px] font-extrabold flex items-center justify-center border-2 border-white">
                    {pendingBikes.length}
                  </span>
                )}
              </button>
            )}
            <button data-testid="help-btn" onClick={() => setHelpOpen(true)}
              className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-wider text-topacio-faded hover:text-topacio-ink transition-colors">
              <BookOpen className="w-4 h-4" /> Ayuda
            </button>
            {canEdit && (
              <button data-testid="office-display-link" onClick={() => navigate("/admin/pantalla")}
                className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-topacio-faded hover:text-topacio-ink transition-colors">
                <Monitor className="w-4 h-4" /> Pantalla
              </button>
            )}
            {canEdit && (
              <button data-testid="deliveries-link" onClick={() => navigate("/entregas")}
                className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-wider text-topacio-faded hover:text-topacio-ink transition-colors">
                <CalendarClock className="w-4 h-4" /> Entregas
              </button>
            )}
            <button data-testid="admin-refresh-btn" onClick={load} className="text-topacio-faded hover:text-topacio-ink transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 text-xs font-bold text-topacio-faded hover:text-topacio-ink transition-colors">
              <LogOut className="w-4 h-4" /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap gap-2 pb-3">
          {TABS.map(([k, label]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 text-xs font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink transition-colors ${tab === k ? "bg-brand text-topacio-ink shadow-[3px_3px_0_0_#111]" : "bg-white text-topacio-faded"}`}>
              {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {role === "viewer" && (
          <div data-testid="viewer-banner" className="card-brutal px-5 py-3 mb-8 bg-brand flex items-center gap-2 text-sm font-extrabold text-topacio-ink">
            <Check className="w-4 h-4" /> Modo consulta: puedes verlo todo, pero no modificar nada.
          </div>
        )}
        {isVendor && (
          <div data-testid="vendor-banner" className="card-brutal px-5 py-3 mb-8 bg-brand flex items-center gap-2 text-sm font-extrabold text-topacio-ink">
            <Store className="w-4 h-4" /> Modo vendedor: consulta la disponibilidad y registra reservas de tienda.
          </div>
        )}
        {isTaller && (
          <div data-testid="taller-banner" className="card-brutal px-5 py-3 mb-8 bg-brand flex items-center gap-2 text-sm font-extrabold text-topacio-ink">
            <Wrench className="w-4 h-4" /> {isMecanico
              ? "Modo mecánico: revisa el calendario y marca las motos como «revisada»."
              : "Modo lavamotos: revisa el calendario y marca las motos como «OK» (limpia)."}
          </div>
        )}
        {tab === "noc" && (
          <div data-testid="noc-board" className="bg-topacio-ink border-2 border-black rounded-xl shadow-[8px_8px_0_0_#FFCC00] p-5 sm:p-8 text-white">
            {!noc ? (
              <p className="text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando centro de operaciones…</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-topacio-green opacity-75" />
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-topacio-green" />
                    </span>
                    <h2 className="font-display font-black uppercase tracking-wide text-lg text-white">Centro de operaciones</h2>
                  </div>
                  <span className="font-mono text-xs text-zinc-500">{new Date(noc.generated_at).toLocaleString("es-ES")}</span>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <NocKpi testid="noc-fleet" label="Flota operativa" icon={Bike} tone="text-topacio-green"
                    value={`${noc.fleet.operative}/${noc.fleet.total}`}
                    sub={`${noc.fleet.workshop} no operativas · ${noc.fleet.coming_soon} próximamente`} />
                  <NocKpi testid="noc-rented" label="En ruta hoy" icon={Activity} tone="text-brand"
                    value={noc.fleet.rented_today} sub="motos con alquiler activo" />
                  <NocKpi testid="noc-bookings-month" label="Reservas este mes" icon={CalendarClock} tone="text-topacio-red"
                    value={noc.bookings.month} sub={`${noc.bookings.week} esta semana · ${noc.bookings.total} totales`} />
                  <NocKpi testid="noc-revenue-month" label="Facturación mes" icon={Euro} tone="text-brand"
                    value={`${(Math.round((noc.revenue.month || 0) * 100) / 100).toFixed(2)}€`} sub={`${(Math.round((noc.revenue.total || 0) * 100) / 100).toFixed(2)}€ acumulado`} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4 gap-4 mb-6">
                  <NocCard title="Estado de flota" icon={Bike} testid="noc-fleet-status">
                    <div className="flex h-5 rounded-full overflow-hidden border-2 border-white/20 mb-4">
                      {[
                        [noc.fleet.operative - noc.fleet.rented_today, "#2A9D8F", "Disponibles"],
                        [noc.fleet.rented_today, "#FFCC00", "En ruta"],
                        [noc.fleet.workshop, "#E63946", "En taller"],
                        [noc.fleet.coming_soon, "#71717a", "Próximamente"],
                      ].filter(([v]) => v > 0).map(([v, c], i) => (
                        <div key={i} style={{ width: `${(v / Math.max(noc.fleet.total, 1)) * 100}%`, background: c }} />
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs font-semibold">
                      {[["Disponibles ahora", noc.fleet.operative - noc.fleet.rented_today, "#2A9D8F"],
                        ["En ruta", noc.fleet.rented_today, "#FFCC00"],
                        ["No operativas", noc.fleet.workshop, "#E63946"],
                        ["Próximamente", noc.fleet.coming_soon, "#71717a"]].map(([l, v, c]) => (
                        <div key={l} className="flex items-center gap-2 text-zinc-300">
                          <span className="w-2.5 h-2.5 rounded-sm border border-black/50" style={{ background: c }} />
                          {l}: <span className="text-white font-extrabold">{v}</span>
                        </div>
                      ))}
                    </div>
                    {noc.fleet.by_status && (
                      <div data-testid="noc-fleet-bystatus" className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-white/10">
                        {Object.entries(noc.fleet.by_status).filter(([, v]) => v > 0).map(([k, v]) => (
                          <span key={k} className="text-[10px] font-extrabold uppercase tracking-wider rounded-md border border-white/25 px-2 py-1 text-zinc-300">
                            {(BIKE_STATUS[k] || { label: k }).label}: <span className="text-brand">{v}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </NocCard>

                  <NocCard title="Ocupación · próximos 30 días" icon={Activity} testid="noc-occupancy">
                    <div className="flex items-end gap-3">
                      <span className="font-display font-black text-5xl text-brand leading-none">{noc.occupancy_30d}%</span>
                      <span className="text-xs text-zinc-500 font-semibold pb-1">de días-moto reservados</span>
                    </div>
                    <div className="mt-4 h-4 rounded-full bg-white/10 border-2 border-white/20 overflow-hidden">
                      <div className="h-full bg-brand transition-all duration-700" style={{ width: `${Math.min(noc.occupancy_30d, 100)}%` }} />
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3 text-xs font-semibold text-zinc-400">
                      <div>Comisiones pendientes: <span className="text-brand font-extrabold">{noc.partners.commission_pending}€</span></div>
                      <div>Reservas de partners: <span className="text-white font-extrabold">{noc.partners.bookings}</span></div>
                      <div>Clientes que vuelven: <span className="text-topacio-green font-extrabold">{noc.loyalty_count}</span></div>
                    </div>
                  </NocCard>

                  <NocCard title="Salud del servidor" icon={HardDrive} testid="noc-server-health">
                    {serverHealth ? (
                      <div className="space-y-3">
                        <span data-testid="server-health-status" className={`badge-brutal !text-[10px] ${serverHealth.status === "ok" ? "bg-topacio-green text-white" : serverHealth.status === "warning" ? "bg-brand text-topacio-ink" : "bg-topacio-red text-white"}`}>
                          {serverHealth.status === "ok" ? "Todo OK" : serverHealth.status === "warning" ? "Atención" : "Crítico"}
                        </span>
                        <div className="space-y-2">
                          {serverHealth.disks.map((d) => (
                            <div key={d.path}>
                              <div className="flex justify-between text-[10px] font-extrabold uppercase tracking-wider text-zinc-400 mb-1">
                                <span>{d.path === "/" ? "Sistema" : "Datos"}</span>
                                <span>{d.used_percent}% · libres {d.free_gb} GB</span>
                              </div>
                              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                <div className={`h-full ${d.used_percent >= 95 ? "bg-topacio-red" : d.used_percent >= 85 ? "bg-brand" : "bg-topacio-green"}`} style={{ width: `${Math.min(d.used_percent, 100)}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="text-[10px] font-bold text-zinc-400 border-t border-white/10 pt-3 space-y-1.5">
                          <p>Backend: <span className={serverHealth.backend.ok ? "text-topacio-green" : "text-topacio-red"}>{serverHealth.backend.ok ? "respondiendo" : "caído"}</span></p>
                          <p>Backup: <span className={serverHealth.backup.ok ? "text-topacio-green" : "text-topacio-red"}>{serverHealth.backup.detail}</span></p>
                          <p className="text-zinc-500">Si algo falla, el watchdog avisa por email a soporte@siempria.com.</p>
                        </div>
                        <HealthHistoryMini history={healthHistory} />
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500 font-semibold">Midiendo servidor…</p>
                    )}
                  </NocCard>

                  <NocCard title="Opciones de reserva" icon={Store} testid="noc-booking-options">
                    <p className="text-xs font-semibold text-zinc-400 leading-relaxed">
                      La web y partners solo ofrecen recogida en la oficina de Costa Adeje.
                    </p>
                    <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-extrabold text-zinc-500">Entrega fuera de oficina</p>
                        <p className={`text-xs font-extrabold mt-1 ${bookingOptions?.delivery_pickup_enabled ? "text-topacio-green" : "text-zinc-500"}`}>
                          {bookingOptions?.delivery_pickup_enabled ? "Activada" : "Desactivada"}
                        </p>
                      </div>
                      <button data-testid="delivery-pickup-toggle" onClick={toggleDeliveryPickup} disabled={!canEdit}
                        className={`relative w-12 h-7 rounded-full border-2 border-white/20 transition-colors ${bookingOptions?.delivery_pickup_enabled ? "bg-topacio-green" : "bg-zinc-700"}`}
                        aria-label="Activar entrega fuera de oficina">
                        <span className={`absolute top-0.5 left-1 w-5 h-5 rounded-full bg-white transition-transform ${bookingOptions?.delivery_pickup_enabled ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-extrabold text-zinc-500 flex items-center gap-1.5"><Wrench className="w-3 h-3" /> Web en el taller</p>
                        <p className={`text-xs font-extrabold mt-1 ${bookingOptions?.maintenance_mode ? "text-topacio-red" : "text-zinc-500"}`}>
                          {bookingOptions?.maintenance_mode ? "Mantenimiento activo" : "Web visible"}
                        </p>
                      </div>
                      <button data-testid="maintenance-mode-toggle" onClick={toggleMaintenanceMode} disabled={!canEdit}
                        className={`relative w-12 h-7 rounded-full border-2 border-white/20 transition-colors ${bookingOptions?.maintenance_mode ? "bg-topacio-red" : "bg-zinc-700"}`}
                        aria-label="Activar página de mantenimiento">
                        <span className={`absolute top-0.5 left-1 w-5 h-5 rounded-full bg-white transition-transform ${bookingOptions?.maintenance_mode ? "translate-x-4" : "translate-x-0"}`} />
                      </button>
                    </div>
                    <p className="text-[10px] text-zinc-500 font-semibold mt-3">Actívalo cuando queráis ofrecer aeropuerto u hotel; el backend y los formularios ya están preparados.</p>
                    <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-extrabold text-zinc-500">Alerta de revisión en pantalla</p>
                        <p className="text-xs font-extrabold mt-1 text-brand" data-testid="review-alert-days-label">{bookingOptions?.review_alert_days || 7} días sin revisar</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button data-testid="review-alert-minus" onClick={() => changeReviewAlertDays(-1)} disabled={!canEdit || (bookingOptions?.review_alert_days || 7) <= 1}
                          className="w-8 h-8 rounded-lg border-2 border-white/20 bg-white/5 text-white font-extrabold hover:bg-white/15 transition-colors disabled:opacity-40">−</button>
                        <button data-testid="review-alert-plus" onClick={() => changeReviewAlertDays(1)} disabled={!canEdit || (bookingOptions?.review_alert_days || 7) >= 90}
                          className="w-8 h-8 rounded-lg border-2 border-white/20 bg-white/5 text-white font-extrabold hover:bg-white/15 transition-colors disabled:opacity-40">+</button>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider font-extrabold text-zinc-500">Recordatorio de devolución</p>
                        <p className="text-xs font-extrabold mt-1 text-brand" data-testid="return-reminder-lead-label">{bookingOptions?.return_reminder_lead_min || 120} min antes</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button data-testid="return-reminder-minus" onClick={() => changeReturnReminderLead(-15)} disabled={!canEdit || (bookingOptions?.return_reminder_lead_min || 120) <= 15}
                          className="w-8 h-8 rounded-lg border-2 border-white/20 bg-white/5 text-white font-extrabold hover:bg-white/15 transition-colors disabled:opacity-40">−</button>
                        <button data-testid="return-reminder-plus" onClick={() => changeReturnReminderLead(15)} disabled={!canEdit || (bookingOptions?.return_reminder_lead_min || 120) >= 360}
                          className="w-8 h-8 rounded-lg border-2 border-white/20 bg-white/5 text-white font-extrabold hover:bg-white/15 transition-colors disabled:opacity-40">+</button>
                      </div>
                    </div>
                    <p className="text-[10px] text-zinc-500 font-semibold mt-2">Email (y WhatsApp si Twilio) al cliente esa antelación antes de su hora de devolución.</p>
                    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3">
                      <p className="text-[10px] uppercase tracking-wider font-extrabold text-zinc-500 flex items-center gap-1.5"><Activity className="w-3 h-3" /> Google Analytics (ID de medición)</p>
                      <p className="text-[10px] text-zinc-500 font-semibold mt-1">Pega el ID GA4 (p. ej. <span className="text-brand font-extrabold">G-XXXXXXXXXX</span>). Lo encuentras en Google Analytics → Administrar → Flujos de datos → tu web → «ID de medición». Se carga solo cuando el visitante acepta las cookies.</p>
                      <div className="mt-2 space-y-2">
                        <input data-testid="ga-id-input" value={gaInput} onChange={(e) => setGaInput(e.target.value)} disabled={!canEdit}
                          placeholder="G-XXXXXXXXXX"
                          className="w-full rounded-lg border-2 border-white/20 bg-white/5 text-white placeholder-zinc-500 px-3 py-2 text-sm font-semibold outline-none focus:border-brand" />
                        <button data-testid="ga-id-save-btn" onClick={saveGaId} disabled={!canEdit || gaSaving}
                          className="btn-primary !text-xs !px-4 !py-2 w-full flex items-center justify-center gap-1.5">
                          {gaSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Guardar"}
                        </button>
                      </div>
                      {bookingOptions?.ga_id && (
                        <p className="text-[10px] text-topacio-green font-extrabold mt-2" data-testid="ga-id-active">Activo: {bookingOptions.ga_id}</p>
                      )}
                    </div>
                  </NocCard>
                  <NocCard title="SES Hospedajes · Ministerio Interior" icon={FileText} testid="noc-ses">
                    {sesInfo ? (
                      <div>
                        <div className="flex gap-2 flex-wrap">
                          <span className="badge-brutal bg-topacio-green text-white !text-[10px]" data-testid="ses-included">{sesInfo.included} listas hoy</span>
                          {sesInfo.pending.length > 0 && (
                            <span className="badge-brutal bg-topacio-red text-white !text-[10px]" data-testid="ses-pending">{sesInfo.pending.length} pendientes</span>
                          )}
                        </div>
                        {sesInfo.pending.length > 0 && (
                          <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                            {sesInfo.pending.map((p) => (
                              <p key={p.ref} className="text-[10px] text-zinc-400 font-semibold">
                                <span className="text-brand font-extrabold">{p.ref}</span> · falta: {p.missing.join(", ")}
                              </p>
                            ))}
                          </div>
                        )}
                        <div className="mt-3 flex items-center gap-2">
                          <input data-testid="ses-date-input" type="date" value={sesDate || sesInfo.date}
                            onChange={(e) => { setSesDate(e.target.value); loadSesPreview(e.target.value); }}
                            className="input-brutal !py-1.5 !px-2.5 !text-xs !w-36 !bg-white/5 !text-white !border-white/20" />
                          <button data-testid="ses-download-btn" onClick={downloadSesXml}
                            className="btn-primary !px-4 !py-2 !text-[10px]">Descargar XML</button>
                        </div>
                        <p className="text-[10px] text-zinc-500 font-semibold mt-2">Se genera solo cada día a las 19:00 y llega por email con las reservas del día. Súbelo en la sede: Hospedajes → Alta masiva.</p>
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500 font-semibold">Cargando…</p>
                    )}
                  </NocCard>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
                  <NocCard title="Reservas · últimos 30 días" icon={CalendarClock} testid="noc-daily-card">
                    <NocBars testid="noc-daily-chart" data={noc.daily} valueKey="n" labelKey="d" />
                  </NocCard>
                  <NocCard title="Facturación · últimos 6 meses" icon={Euro} testid="noc-revenue-card">
                    <NocBars testid="noc-revenue-chart" data={noc.revenue.monthly} valueKey="v" labelKey="m" money every={1} />
                  </NocCard>
                  <NocCard title="Canales de reserva" icon={Store} testid="noc-channels-card">
                    <NocChannels channels={noc.channels} />
                  </NocCard>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <NocCard title="Top motos" icon={Trophy} testid="noc-top-bikes">
                    {noc.top_bikes.length === 0 ? (
                      <p className="text-xs text-zinc-500 font-semibold">Sin reservas todavía.</p>
                    ) : (
                      <div className="space-y-2.5">
                        {noc.top_bikes.map((b, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <span className="font-mono text-[10px] text-zinc-500 w-4">0{i + 1}</span>
                            <div className="flex-1">
                              <div className="flex justify-between text-xs font-bold text-zinc-300 mb-1">
                                <span className="truncate">{b.name}</span><span className="text-brand">{b.n}</span>
                              </div>
                              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full bg-topacio-green" style={{ width: `${(b.n / noc.top_bikes[0].n) * 100}%` }} />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </NocCard>

                  <NocCard title="Hoy: entregas y devoluciones" icon={ArrowRightLeft} testid="noc-today">
                    {noc.pickups_today.length === 0 && noc.returns_today.length === 0 ? (
                      <p className="text-xs text-zinc-500 font-semibold">Sin movimientos hoy. ¡Buen día para pasear!</p>
                    ) : (
                      <div className="space-y-2 text-xs font-semibold">
                        {noc.pickups_today.map((p) => (
                          <div key={`p-${p.ref}`} className="flex items-center gap-2 text-zinc-300">
                            <span className="badge-brutal bg-topacio-green text-white !text-[9px]">Entrega</span>
                            <span className="truncate">{p.name} · {p.bike}</span>
                          </div>
                        ))}
                        {noc.returns_today.map((p) => (
                          <div key={`r-${p.ref}`} className="flex items-center gap-2 text-zinc-300">
                            <span className="badge-brutal bg-topacio-red text-white !text-[9px]">Devolución</span>
                            <span className="truncate">{p.name} · {p.bike}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </NocCard>

                  <NocCard title="Facturación por grupo" icon={Euro} testid="noc-by-group">
                    {noc.revenue.by_group.length === 0 ? (
                      <p className="text-xs text-zinc-500 font-semibold">Aún sin facturación por grupos.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {noc.revenue.by_group.map((g) => (
                          <span key={g.g} className="badge-brutal bg-brand text-topacio-ink !text-xs">
                            {g.g} · {g.v}€
                          </span>
                        ))}
                      </div>
                    )}
                  </NocCard>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
                  <NocCard title="Podio de colaboradores · este mes" icon={Handshake} testid="noc-partner-podium">
                    {(noc.partner_ranking || []).length === 0 ? (
                      <p className="text-xs text-zinc-500 font-semibold">Sin colaboradores activos todavía.</p>
                    ) : (
                      <div className="space-y-2.5">
                        {noc.partner_ranking.map((p, i) => {
                          const medal = ["text-brand", "text-zinc-300", "text-amber-600"][i] || "text-zinc-600";
                          const pct = p.goal ? Math.min(100, Math.round((p.n / p.goal) * 100)) : null;
                          return (
                            <div key={p.code} data-testid={`podium-row-${p.code}`} className="flex items-center gap-3">
                              <Trophy className={`w-4 h-4 shrink-0 ${medal}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between text-xs font-bold text-zinc-300 mb-1">
                                  <span className="truncate">{p.name}</span>
                                  <span className="text-brand shrink-0">{p.n} reservas · {p.revenue}€</span>
                                </div>
                                {pct !== null && (
                                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                    <div className={`h-full ${p.n >= p.goal ? "bg-topacio-green" : "bg-brand"}`} style={{ width: `${pct}%` }} />
                                  </div>
                                )}
                                <p className="text-[9px] text-zinc-500 font-semibold mt-0.5">
                                  Comisión: {p.commission}€{p.goal ? ` · Meta: ${p.n}/${p.goal}` : ""}{p.n >= (p.goal || Infinity) && p.bonus ? ` · +${p.bonus}% bonus activo` : ""}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </NocCard>
                </div>
              </>
            )}
          </div>
        )}

        {tab === "bookings" && (
          <>
            {stats && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                {[
                  ["Reservas totales", stats.total, "admin-stat-total"],
                  ["Pendientes", stats.pending, "admin-stat-pending"],
                  ["Pagadas/Confirmadas", stats.paid, "admin-stat-paid"],
                  ["Ingresos estimados", `${stats.revenue}€`, "admin-stat-revenue"],
                ].map(([label, value, tid]) => (
                  <div key={tid} data-testid={tid} className="card-brutal px-6 py-5">
                    <div className="font-display font-black text-2xl text-topacio-ink">{value}</div>
                    <div className="text-[11px] text-topacio-faded uppercase tracking-wider font-bold mt-1">{label}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Eye className="w-4 h-4 text-topacio-faded absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input data-testid="bookings-search" value={bkSearch} onChange={(e) => setBkSearch(e.target.value)}
                  placeholder="Buscar por cliente, moto, ref, teléfono…"
                  className="input-brutal !pl-9 !py-2.5 !text-sm w-full" />
              </div>
              <select data-testid="bookings-status-filter" value={bkStatusFilter} onChange={(e) => setBkStatusFilter(e.target.value)}
                className="input-brutal !py-2.5 !w-auto !text-sm">
                <option value="all">Todos los estados</option>
                {Object.keys(STATUS_LABELS).map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
              </select>
              <button data-testid="bookings-hide-completed" onClick={() => setHideCompleted((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-lg border-2 border-topacio-ink px-3 py-2 text-xs font-extrabold uppercase tracking-wider transition-colors ${hideCompleted ? "bg-topacio-ink text-white" : "bg-white text-topacio-ink hover:bg-topacio-sand"}`}>
                {hideCompleted ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />} Ocultar completadas
              </button>
              <button data-testid="bookings-hide-cancelled" onClick={() => setHideCancelled((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-lg border-2 border-topacio-ink px-3 py-2 text-xs font-extrabold uppercase tracking-wider transition-colors ${hideCancelled ? "bg-topacio-ink text-white" : "bg-white text-topacio-ink hover:bg-topacio-sand"}`}>
                {hideCancelled ? <Check className="w-4 h-4" /> : <Ban className="w-4 h-4" />} Ocultar canceladas
              </button>
              {!isVendor && (
                <div className="flex items-center gap-1.5">
                  <input data-testid="bookings-print-month" type="month" value={printMonth} onChange={(e) => setPrintMonth(e.target.value)}
                    className="input-brutal !py-2.5 !w-auto !text-sm" />
                  <button data-testid="bookings-print-btn" onClick={printBookingsMonth}
                    title="Imprimir las reservas de este mes"
                    className="btn-secondary !px-3 !py-2.5 !text-xs flex items-center gap-1.5"><FileText className="w-4 h-4" /> Imprimir mes</button>
                </div>
              )}
              {(canEdit || isVendor) && (
                <button data-testid="add-manual-booking-btn" onClick={() => setManualOpen(true)} className="btn-primary !px-5 !py-2.5 !text-xs">
                  <Store className="w-4 h-4" /> Reserva de tienda
                </button>
              )}
            </div>
            <div className="text-[11px] text-topacio-faded font-bold uppercase tracking-wider mb-2" data-testid="bookings-count">
              {filteredBookings.length} de {bookings.length} reservas
            </div>
            <div className="card-brutal overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                    {(isVendor
                      ? ["Ref", "Cliente", "Moto", "Fechas", "Días", "Pago", "Estado"]
                      : ["Ref", "Cliente", "Moto", "Fechas", "Días", "Total", "Pago", "Alquiler", "Estado"]).map((h) => (
                      <th key={h} className="px-5 py-4 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {filteredBookings.map((bk) => (
                    <tr key={bk.ref} data-testid={`booking-row-${bk.ref}`} className="hover:bg-topacio-sand transition-colors">
                      <td className="px-5 py-4 font-mono text-topacio-red font-extrabold">
                        {canEdit ? (
                          <button data-testid={`booking-detail-btn-${bk.ref}`} onClick={() => setDetailRef(bk.ref)}
                            title="Ver detalles de la reserva"
                            className="inline-flex items-center gap-1 hover:underline underline-offset-2">
                            <Eye className="w-3.5 h-3.5" /> {bk.ref}
                          </button>
                        ) : bk.ref}
                        <span data-testid={`channel-badge-${bk.ref}`}
                          className={`block mt-1 w-fit text-[9px] font-extrabold uppercase tracking-wider rounded-md border-2 border-topacio-ink px-1.5 py-0.5 ${
                            (bk.channel || (bk.partner_code ? "partner" : "online")) === "tienda" ? "bg-topacio-red text-white"
                            : (bk.channel || (bk.partner_code ? "partner" : "online")) === "partner" ? "bg-brand text-topacio-ink"
                            : "bg-zinc-100 text-topacio-faded"}`}>
                          {(bk.channel || (bk.partner_code ? "partner" : "online")) === "tienda" ? "Tienda"
                            : (bk.channel || (bk.partner_code ? "partner" : "online")) === "partner" ? `Partner ${bk.partner_code || ""}`
                            : "Web"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-topacio-ink font-bold">{bk.name}</div>
                        <div className="text-xs text-topacio-faded">{bk.email} · {bk.phone}</div>
                        {bk.hotel_name && <div data-testid={`hotel-line-${bk.ref}`} className="text-xs text-topacio-red font-bold">Entrega: Hotel {bk.hotel_name}</div>}
                        {bk.coupon_code && <div className="text-xs text-topacio-green font-bold">Cupón {bk.coupon_code} (-{bk.discount}€)</div>}
                        {bk.precheckin_status === "completed" ? (
                          <span data-testid={`precheckin-done-${bk.ref}`} className="mt-1.5 w-fit badge-brutal bg-topacio-green text-white !text-[9px]">Check-in docs OK</span>
                        ) : bk.precheckin_status === "pending" ? (
                          <button data-testid={`precheckin-resend-${bk.ref}`} disabled={!canEdit} onClick={() => resendPrecheckin(bk.ref)}
                            title={bk.precheckin_sent_at ? `Último envío ${bk.precheckin_sent_at.slice(0, 16).replace("T", " ")}` : "Reenviar email de precheck-in"}
                            className={`mt-1.5 flex items-center gap-1 text-[9px] font-extrabold uppercase tracking-wider rounded-md border-2 border-topacio-ink px-1.5 py-0.5 transition-colors ${canEdit ? "bg-brand text-topacio-ink hover:-translate-y-0.5" : "bg-zinc-100 text-topacio-faded cursor-not-allowed"}`}>
                            <MailCheck className="w-3 h-3" /> Docs pendientes · reenviar
                          </button>
                        ) : null}
                      </td>
                      <td className="px-5 py-4 text-topacio-ink font-semibold">{bk.bike_name}</td>
                      <td className="px-5 py-4 text-topacio-faded whitespace-nowrap">{bk.start_date} → {bk.end_date}</td>
                      <td className="px-5 py-4 text-topacio-faded">{bk.days}</td>
                      {isVendor && (() => {
                        const total = Number(bk.total) || 0;
                        const paid = bk.payment_status === "deposit_paid" ? (bk.advance_amount != null ? Number(bk.advance_amount) : Math.round(total * 0.5)) : 0;
                        const remaining = Math.max(0, total - paid);
                        return (
                          <td className="px-5 py-4" data-testid={`vendor-pay-${bk.ref}`}>
                            <div className="text-xs font-extrabold text-topacio-ink">Total {total}€</div>
                            <div className={`text-[11px] font-bold ${paid > 0 ? "text-topacio-green" : "text-zinc-400"}`}>Pagado {paid}€{bk.payment_status === "in_store" ? " · en tienda" : ""}</div>
                            <div className={`text-[11px] font-extrabold ${remaining > 0 ? "text-topacio-red" : "text-topacio-green"}`}>Resto {remaining}€</div>
                          </td>
                        );
                      })()}
                      {!isVendor && (
                        <td className="px-5 py-4">
                          <div className="font-extrabold text-topacio-ink">{bk.total}€</div>
                          {bk.commission_eur != null && (
                            <button data-testid={`commission-toggle-${bk.ref}`}
                              onClick={() => {
                                if (!canEdit) return;
                                if (bk.commission_paid) {
                                  if (window.confirm("¿Devolver esta comisión a pendiente?")) unmarkCommissionPaid(bk.ref);
                                } else setCommissionFor(bk);
                              }}
                              disabled={!canEdit}
                              title={bk.partner_name || bk.partner_code}
                              className={`mt-1.5 text-[10px] font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-2.5 py-1 transition-colors ${bk.commission_paid ? "bg-topacio-green text-white" : "bg-brand text-topacio-ink hover:bg-brand-hover"} ${!canEdit ? "opacity-70 cursor-not-allowed" : ""}`}>
                              Com. {bk.commission_eur}€ · {bk.commission_paid
                                ? `Cobrada ${bk.commission_paid_at || ""}${bk.commission_method ? ` · ${bk.commission_method}` : ""}`
                                : "Liquidar"}
                            </button>
                          )}
                        </td>
                      )}
                      {!isVendor && (
                        <td className="px-5 py-4">
                          <span className={`text-xs font-extrabold uppercase tracking-wider ${bk.payment_status === "deposit_paid" ? "text-topacio-green" : "text-zinc-400"}`}>
                            {bk.payment_status === "deposit_paid" ? `${bk.advance_amount != null ? bk.advance_amount : Math.round((bk.total || 0) * 0.5)}€ pagado` : bk.payment_status === "in_store" ? "En tienda" : "Sin pagar"}
                          </span>
                        </td>
                      )}
                      {!isVendor && (
                        <td className="px-5 py-4">
                          <div className="flex flex-col gap-1.5 items-start">
                            <div className="flex gap-1.5 items-center">
                              <ContractButton bookingRef={bk.ref} headers={headers} />
                              <button data-testid={`deposit-btn-${bk.ref}`} disabled={!canEdit} onClick={() => canEdit && setDepositFor(bk)}
                                title="Gestionar fianza (importe, método, devolución)"
                                className={`text-[10px] font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-2 py-1 transition-colors ${(DEPOSIT_BADGE[bk.deposit_status] || DEPOSIT_BADGE.pendiente)[1]} ${!canEdit ? "opacity-70 cursor-not-allowed" : "hover:-translate-y-0.5"}`}>
                                {(DEPOSIT_BADGE[bk.deposit_status] || DEPOSIT_BADGE.pendiente)[0]}
                                {bk.deposit_amount != null && bk.deposit_status ? ` ${bk.deposit_amount}€` : ""}
                              </button>
                            </div>
                            {bk.deposit_status === "parcial" && bk.deposit_retained > 0 && (
                              <span className="text-[9px] font-bold text-topacio-red">Retenidos {bk.deposit_retained}€{bk.deposit_note ? ` · ${bk.deposit_note.slice(0, 40)}` : ""}</span>
                            )}
                            {bk.cancellation_fee != null && (
                              <span data-testid={`cancel-fee-${bk.ref}`} className="text-[9px] font-bold text-topacio-faded">Cancelación: {bk.cancellation_fee}€</span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="px-5 py-4">
                        <select data-testid={`status-select-${bk.ref}`} value={bk.status} disabled={!canEdit}
                          onChange={(e) => setStatus(bk.ref, e.target.value)}
                          className={`text-xs font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-2 py-1.5 outline-none ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-80"} ${STATUS_COLORS[bk.status]}`}>
                          {Object.keys(STATUS_LABELS).map((s) => (
                            <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                          ))}
                        </select>
                        {!isVendor && bk.delivery_status === "entregada" && (
                          <span data-testid={`delivered-badge-${bk.ref}`} className="block mt-1.5 w-fit text-[9px] font-extrabold uppercase tracking-wider rounded-md border-2 border-topacio-ink px-1.5 py-0.5 bg-topacio-green text-white"
                            title={`Entregada por ${bk.delivered_by || ""} ${bk.delivered_at ? `el ${bk.delivered_at.slice(0, 16).replace("T", " ")}` : ""}`}>
                            Moto entregada
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filteredBookings.length === 0 && (
                    <tr><td colSpan={isVendor ? 7 : 9} className="px-5 py-12 text-center text-topacio-faded">{bookings.length === 0 ? "Aún no hay reservas." : "Ninguna reserva coincide con el filtro."}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "fleet" && (
          <>
            <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
              <h2 className="font-display text-xl font-bold text-topacio-ink">Flota ({bikes.length} motos)</h2>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Trophy className="w-4 h-4 text-topacio-faded" />
                  <select data-testid="fleet-sort-select" value={fleetSort} onChange={(e) => setFleetSort(e.target.value)}
                    className="input-brutal !py-1.5 !px-3 !text-xs !w-auto">
                    <option value="pizarra">Orden: pizarra (personalizado)</option>
                    <option value="name">Orden: nombre</option>
                    <option value="rentals">Orden: más alquiladas</option>
                    <option value="revenue">Orden: más facturación</option>
                  </select>
                </div>
                {canEdit && (
                  <div className="flex gap-2">
                    <button data-testid="manage-tariffs-btn" onClick={() => setTab("groups")} className="btn-secondary !px-5 !py-2.5 !text-xs">
                      Gestionar tarifas
                    </button>
                    <button data-testid="add-bike-btn" onClick={() => { setEditing(null); setEditorOpen(true); }} className="btn-primary !px-5 !py-2.5 !text-xs">
                      <Plus className="w-4 h-4" /> Añadir moto
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="card-brutal overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                    {["Moto", "Grupo", "1 día", "3 días", "7 días", "Fianza", "Estado", ""].map((h) => (
                      <th key={h} className="px-5 py-4 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {[...bikes].sort((a, b) => {
                    if (isTaller) {
                      const pa = tallerPendingStatuses.includes(a.status || "ok") ? 0 : 1;
                      const pb = tallerPendingStatuses.includes(b.status || "ok") ? 0 : 1;
                      if (pa !== pb) return pa - pb; // pendientes del taller, arriba del todo
                    }
                    if (fleetSort === "rentals") return (b.bookings_count || 0) - (a.bookings_count || 0);
                    if (fleetSort === "revenue") return (b.revenue_total || 0) - (a.revenue_total || 0);
                    if (fleetSort === "name") return a.name.localeCompare(b.name);
                    // pizarra (personalizado): orden guardado del equipo
                    const ao = typeof a.sort_order === "number" ? a.sort_order : 10000;
                    const bo = typeof b.sort_order === "number" ? b.sort_order : 10000;
                    return ao - bo;
                  }).map((bk, idx, arr) => {
                    const tallerPending = isTaller && tallerPendingStatuses.includes(bk.status || "ok");
                    return (
                    <tr key={bk.slug} data-testid={`bike-row-${bk.slug}`} className={`transition-colors ${bk.active === false ? "opacity-40" : ""} ${tallerPending ? "bg-brand/20 ring-2 ring-inset ring-brand" : "hover:bg-topacio-sand"}`}>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <img src={bk.image} alt={bk.name} onError={(e) => { e.currentTarget.src = "/logo-topacio.jpg"; }} className="w-14 h-10 object-cover rounded border border-zinc-200 bg-white" />
                          <span className="font-bold text-topacio-ink">{bk.name}</span>
                          {tallerPending && (
                            <span data-testid={`bike-pending-tag-${bk.slug}`} className="badge-brutal bg-topacio-red text-white !text-[9px] whitespace-nowrap">{isMecanico ? "POR REVISAR" : "POR LIMPIAR"}</span>
                          )}
                          {(bk.stock || 1) > 1 && (
                            <span data-testid={`bike-stock-${bk.slug}`} className="badge-brutal bg-topacio-ink text-brand !text-[9px] whitespace-nowrap">{bk.stock} uds</span>
                          )}
                          {(bk.bookings_count > 0 || bk.revenue_total > 0) && (
                            <span data-testid={`bike-stats-${bk.slug}`} className="text-[10px] font-extrabold text-topacio-faded whitespace-nowrap">
                              {bk.bookings_count} alq · <span className="text-topacio-green">{bk.revenue_total}€</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-3"><span className="badge-brutal bg-brand text-topacio-ink">{bk.group}</span></td>
                      <td className="px-5 py-3 font-semibold">{bk.price_1d}€</td>
                      <td className="px-5 py-3 font-semibold">{bk.price_3d}€</td>
                      <td className="px-5 py-3 font-semibold">{bk.price_7d}€</td>
                      <td className="px-5 py-3 text-topacio-faded">{bk.deposit}€</td>
                      <td className="px-5 py-3">
                        {bk.rented_now && (
                          <span data-testid={`bike-rented-${bk.slug}`} className="badge-brutal bg-topacio-green text-white !text-[9px] mb-1.5 block w-fit">{(bk.stock || 1) > 1 ? `${bk.units_rented_now}/${bk.stock} en la calle` : "Alquilada ahora"}</span>
                        )}
                        {canEdit ? (
                          <select data-testid={`bike-status-${bk.slug}`} value={bk.status || "ok"} onChange={(e) => requestBikeStatus(bk.slug, e.target.value)}
                            className={`text-[10px] font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-2 py-1.5 outline-none cursor-pointer ${(BIKE_STATUS[bk.status || "ok"] || BIKE_STATUS.ok).cls}`}>
                            {Object.entries(BIKE_STATUS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
                          </select>
                        ) : isTaller ? (
                          <div className="flex flex-col gap-1.5 items-start">
                            <span className={`badge-brutal ${(BIKE_STATUS[bk.status || "ok"] || BIKE_STATUS.ok).cls}`}>
                              {(BIKE_STATUS[bk.status || "ok"] || BIKE_STATUS.ok).label}
                            </span>
                            {(bk.status || "ok") !== tallerStatus && (
                              <button data-testid={`bike-taller-mark-${bk.slug}`}
                                onClick={() => patchBikeStatus(bk.slug, { status: tallerStatus, note: "", repair_cost: null })}
                                className="text-[10px] font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-2.5 py-1.5 bg-white hover:bg-brand transition-colors flex items-center gap-1">
                                <Check className="w-3 h-3" /> {isMecanico ? "Marcar revisada" : "Marcar OK"}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className={`badge-brutal ${(BIKE_STATUS[bk.status || "ok"] || BIKE_STATUS.ok).cls}`}>
                            {(BIKE_STATUS[bk.status || "ok"] || BIKE_STATUS.ok).label}
                          </span>
                        )}
                        {bk.status_updated_by && (
                          <div data-testid={`bike-status-by-${bk.slug}`} className="text-[10px] text-topacio-faded font-semibold mt-1">
                            {statusActionLabel(bk.status || "ok")} <span className="font-extrabold text-topacio-ink">{bk.status_updated_by}</span>
                            {bk.status_updated_at ? ` · ${fmtAgo(bk.status_updated_at)}` : ""}
                          </div>
                        )}
                        {bk.status_note && bk.status !== "ok" && (
                          <div data-testid={`bike-status-note-${bk.slug}`} className="text-[10px] text-topacio-faded font-bold mt-1 max-w-[180px]">{bk.status_note}</div>
                        )}
                        {bk.repair_cost != null && (bk.status || "ok") !== "ok" && (
                          <div className="text-[10px] text-topacio-red font-extrabold mt-0.5">~{bk.repair_cost}€ reparación</div>
                        )}
                        <span className={`block mt-1 text-[9px] font-extrabold uppercase ${bk.active !== false ? "text-topacio-green" : "text-topacio-red"}`}>
                          {bk.active !== false ? "Visible en la web" : "Oculta en la web"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        {canEdit && (
                          <div className="flex gap-2">
                            {fleetSort === "pizarra" && (
                              <div className="flex flex-col">
                                <button data-testid={`bike-move-up-${bk.slug}`} onClick={() => moveBike(bk.slug, -1)} disabled={idx === 0}
                                  title="Subir en la pizarra"
                                  className="w-8 h-4 bg-white border-2 border-topacio-ink rounded-t-lg flex items-center justify-center hover:bg-brand transition-colors disabled:opacity-30">
                                  <ArrowUp className="w-3 h-3 text-topacio-ink" />
                                </button>
                                <button data-testid={`bike-move-down-${bk.slug}`} onClick={() => moveBike(bk.slug, 1)} disabled={idx === arr.length - 1}
                                  title="Bajar en la pizarra"
                                  className="w-8 h-4 bg-white border-2 border-t-0 border-topacio-ink rounded-b-lg flex items-center justify-center hover:bg-brand transition-colors disabled:opacity-30">
                                  <ArrowDown className="w-3 h-3 text-topacio-ink" />
                                </button>
                              </div>
                            )}
                            <button data-testid={`edit-bike-${bk.slug}`} onClick={() => { setEditing(bk); setEditorOpen(true); }}
                              className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
                              <Pencil className="w-3.5 h-3.5 text-topacio-ink" />
                            </button>
                            {bk.active !== false && (
                              <button data-testid={`delete-bike-${bk.slug}`} onClick={() => deleteBike(bk.slug)}
                                className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-topacio-red hover:text-white transition-colors">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "groups" && (
          <>
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-display text-xl font-bold text-topacio-ink">Grupos de tarifa ({groups.length})</h2>
              {canEdit && (
                <button data-testid="add-group-btn" onClick={() => setGroupForm({ letter: "", license: "A1 / B+3", price_1d: "", price_3d: "", price_7d: "", deposit: "", insurance_per_day: "", franchise: "", isNew: true, apply: false })}
                  className="btn-primary !px-5 !py-2.5 !text-xs">
                  <Plus className="w-4 h-4" /> Nuevo grupo
                </button>
              )}
            </div>

            {groupForm && (
              <div className="card-brutal p-6 mb-8" data-testid="group-form">
                <h3 className="font-display text-base font-bold text-topacio-ink mb-5">
                  {groupForm.isNew ? "Crear grupo de tarifa" : `Editar grupo ${groupForm.letter}`}
                </h3>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Letra</label>
                    <input data-testid="group-letter-input" value={groupForm.letter} disabled={!groupForm.isNew}
                      onChange={(e) => setGroupForm({ ...groupForm, letter: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="L" className="input-brutal mt-1 !w-20 uppercase disabled:opacity-60" />
                  </div>
                  <div>
                    <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Carnet</label>
                    <input data-testid="group-license-input" value={groupForm.license}
                      onChange={(e) => setGroupForm({ ...groupForm, license: e.target.value })}
                      list="license-options" className="input-brutal mt-1 !w-36" />
                    <datalist id="license-options">
                      <option value="A1 / B+3" /><option value="A2" /><option value="A" />
                    </datalist>
                  </div>
                  {[["price_1d", "Precio 1 día"], ["price_3d", "Precio 3 días"], ["price_7d", "Precio 7 días"], ["deposit", "Fianza"], ["insurance_per_day", "Seguro €/día"], ["franchise", "Franquicia €"]].map(([k, label]) => (
                    <div key={k}>
                      <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">{label}</label>
                      <input data-testid={`group-${k}-input`} type="number" min="0" value={groupForm[k]}
                        onChange={(e) => setGroupForm({ ...groupForm, [k]: e.target.value })} className="input-brutal mt-1 !w-28" />
                    </div>
                  ))}
                </div>
                {!groupForm.isNew && (
                  <label className="flex items-center gap-2.5 mt-5 text-sm font-semibold text-topacio-ink cursor-pointer select-none">
                    <input data-testid="group-apply-checkbox" type="checkbox" checked={groupForm.apply}
                      onChange={(e) => setGroupForm({ ...groupForm, apply: e.target.checked })} className="w-4 h-4 accent-[#FFCC00]" />
                    Aplicar estos precios a las {bikes.filter((b) => b.group === groupForm.letter).length} motos del grupo {groupForm.letter}
                  </label>
                )}
                <div className="flex gap-3 mt-5">
                  <button data-testid="group-save-btn" onClick={saveGroup}
                    disabled={!groupForm.letter || !groupForm.price_1d}
                    className="btn-primary !px-6 !py-3 !text-xs">
                    <Check className="w-4 h-4" /> {groupForm.isNew ? "Crear grupo" : "Guardar cambios"}
                  </button>
                  <button data-testid="group-cancel-btn" onClick={() => setGroupForm(null)} className="btn-secondary !px-5 !py-3 !text-xs">Cancelar</button>
                </div>
              </div>
            )}

            <div className="card-brutal overflow-x-auto">
              <table className="w-full text-sm min-w-[760px]">
                <thead>
                  <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                    {["Grupo", "Carnet", "1 día", "3 días", "7 días", "Fianza", "Motos", ""].map((h) => (
                      <th key={h} className="px-5 py-4 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {groups.map((g) => {
                    const count = bikes.filter((b) => b.group === g.letter).length;
                    return (
                      <tr key={g.letter} data-testid={`group-row-${g.letter}`} className="hover:bg-topacio-sand transition-colors">
                        <td className="px-5 py-4"><span className="badge-brutal bg-brand text-topacio-ink !text-sm">{g.letter}</span></td>
                        <td className="px-5 py-4 font-semibold text-topacio-ink">{g.license}</td>
                        <td className="px-5 py-4 font-semibold">{g.price_1d}€</td>
                        <td className="px-5 py-4 font-semibold">{g.price_3d}€</td>
                        <td className="px-5 py-4 font-semibold">{g.price_7d}€</td>
                        <td className="px-5 py-4 text-topacio-faded">{g.deposit}€</td>
                        <td className="px-5 py-4 text-topacio-faded">{count}</td>
                        <td className="px-5 py-4">
                          {canEdit && (
                            <div className="flex gap-2">
                              <button data-testid={`edit-group-${g.letter}`}
                                onClick={() => setGroupForm({ ...g, isNew: false, apply: false })}
                                className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
                                <Pencil className="w-3.5 h-3.5 text-topacio-ink" />
                              </button>
                              {count === 0 && (
                                <button data-testid={`delete-group-${g.letter}`} onClick={() => deleteGroup(g.letter)}
                                  className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-topacio-red hover:text-white transition-colors">
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-topacio-faded font-semibold mt-4">Las motos usan la letra de su grupo en la ficha de alta; al editar un grupo puedes propagar sus precios a todas sus motos de una vez.</p>
          </>
        )}

        {tab === "coupons" && (
          <>
            {canEdit && (
            <div className="card-brutal p-6 mb-8">
              <h2 className="font-display text-lg font-bold text-topacio-ink flex items-center gap-2 mb-5">
                <TicketPercent className="w-5 h-5 text-topacio-red" /> Crear cupón
              </h2>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Código</label>
                  <input data-testid="coupon-code-input" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })}
                    placeholder="TOPACIO20" className="input-brutal mt-1 uppercase !w-48" />
                </div>
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Tipo</label>
                  <select data-testid="coupon-type-select" value={newCoupon.type} onChange={(e) => setNewCoupon({ ...newCoupon, type: e.target.value })}
                    className="input-brutal mt-1 !w-40">
                    <option value="percent">% porcentaje</option>
                    <option value="fixed">€ fijo</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Valor</label>
                  <input data-testid="coupon-value-input" type="number" min="1" value={newCoupon.value} onChange={(e) => setNewCoupon({ ...newCoupon, value: parseFloat(e.target.value) || 0 })}
                    className="input-brutal mt-1 !w-28" />
                </div>
                <button data-testid="coupon-create-btn" onClick={createCoupon} className="btn-primary !px-5 !py-3 !text-xs">
                  <Plus className="w-4 h-4" /> Crear
                </button>
              </div>
            </div>
            )}
            <div className="card-brutal overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                    {["Código", "Descuento", "Usos", "Estado", ""].map((h) => (
                      <th key={h} className="px-5 py-4 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {coupons.map((c) => (
                    <tr key={c.code} data-testid={`coupon-row-${c.code}`} className="hover:bg-topacio-sand transition-colors">
                      <td className="px-5 py-4 font-mono font-extrabold text-topacio-ink">{c.code}</td>
                      <td className="px-5 py-4 font-bold text-topacio-red">{c.type === "percent" ? `${c.value}%` : `${c.value}€`}</td>
                      <td className="px-5 py-4 text-topacio-faded">{c.uses}</td>
                      <td className="px-5 py-4">
                        <button data-testid={`coupon-toggle-${c.code}`} onClick={() => canEdit && toggleCoupon(c)} disabled={!canEdit}
                          className={`text-xs font-extrabold uppercase tracking-wider rounded-lg border-2 border-topacio-ink px-3 py-1.5 transition-colors ${c.active ? "bg-topacio-green text-white" : "bg-zinc-200 text-topacio-faded"} ${!canEdit ? "cursor-not-allowed" : ""}`}>
                          {c.active ? "Activo" : "Inactivo"}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        {canEdit && (
                          <button data-testid={`coupon-delete-${c.code}`} onClick={() => deleteCoupon(c.code)}
                            className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-topacio-red hover:text-white transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {coupons.length === 0 && (
                    <tr><td colSpan={5} className="px-5 py-12 text-center text-topacio-faded">No hay cupones creados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "photos" && (
          <>
            <h2 className="font-display text-xl font-bold text-topacio-ink mb-8">Fotos de clientes ({photos.length})</h2>
            {photos.length === 0 ? (
              <div className="card-brutal p-12 text-center text-topacio-faded">Aún no se han subido fotos.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {photos.map((p) => {
                  const [label, cls] = PHOTO_STATUS[p.status] || PHOTO_STATUS.pending;
                  return (
                    <div key={p.id} data-testid={`admin-photo-${p.id}`} className="card-brutal overflow-hidden">
                      {p.kind === "video" && p.static_url ? (
                        <video src={p.static_url} poster={p.poster_url || undefined} controls preload="metadata"
                          data-testid={`admin-video-${p.id}`}
                          className="w-full aspect-[4/3] object-cover border-b-2 border-topacio-ink bg-black" />
                      ) : (
                        <img src={p.static_url || `${API}/admin/gallery/image/${p.id}?auth=${token}`} alt={`Foto de ${p.name}`}
                          className="w-full aspect-[4/3] object-cover border-b-2 border-topacio-ink" />
                      )}
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-topacio-ink text-sm">{p.name}</p>
                            <p className="text-xs text-topacio-faded font-mono">{p.booking_ref}</p>
                          </div>
                          <span className={`badge-brutal ${cls}`}>{label}</span>
                        </div>
                        {p.status !== "approved" && canEdit && (
                          <div className="flex gap-2 mt-4">
                            <button data-testid={`approve-photo-${p.id}`} onClick={() => setPhotoStatus(p.id, "approved")}
                              className="flex-1 btn-primary !py-2 !text-[11px]">
                              <Check className="w-3.5 h-3.5" /> Publicar
                            </button>
                            {p.status !== "rejected" && (
                              <button data-testid={`reject-photo-${p.id}`} onClick={() => setPhotoStatus(p.id, "rejected")}
                                className="flex-1 btn-red !py-2 !text-[11px]">
                                <Ban className="w-3.5 h-3.5" /> Rechazar
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === "reviews" && reviewStats && (
          <>
            <h2 className="font-display text-xl font-bold text-topacio-ink mb-8">Panel de valoraciones</h2>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
              {[
                ["Recordatorios enviados", reviewStats.sent_total, "reviewstat-sent"],
                ["Enviados esta semana", reviewStats.sent_week, "reviewstat-week"],
                ["Pendientes de envío", reviewStats.pending, "reviewstat-pending"],
                ["Fotos de clientes", reviewStats.photos_total, "reviewstat-photos"],
                ["Fotos esta semana", reviewStats.photos_week, "reviewstat-photos-week"],
                ["Fotos por moderar", reviewStats.photos_pending, "reviewstat-photos-pending"],
                ["Enviados por WhatsApp", reviewStats.wa_sent ?? 0, "reviewstat-wa"],
              ].map(([label, value, tid]) => (
                <div key={tid} data-testid={tid} className="card-brutal px-6 py-5">
                  <div className="font-display font-black text-2xl text-topacio-ink">{value}</div>
                  <div className="text-[11px] text-topacio-faded uppercase tracking-wider font-bold mt-1">{label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="card-brutal overflow-hidden">
                <div className="bg-topacio-ink px-6 py-4">
                  <h3 className="font-display font-bold text-white text-sm flex items-center gap-2 uppercase tracking-wide">
                    <Star className="w-4 h-4 text-brand" /> Últimos recordatorios enviados
                  </h3>
                </div>
                <div className="divide-y divide-zinc-100">
                  {reviewStats.recent.length === 0 && (
                    <p className="px-6 py-8 text-sm text-topacio-faded">Aún no se ha enviado ninguno. Se envían solos 2 días después de cada devolución.</p>
                  )}
                  {reviewStats.recent.map((r) => (
                    <div key={r.ref} data-testid={`reminder-${r.ref}`} className="px-6 py-3.5 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-topacio-ink truncate">{r.name} <span className="font-mono text-xs text-topacio-red">{r.ref}</span></p>
                        <p className="text-xs text-topacio-faded truncate">{r.bike_name}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="badge-brutal bg-white text-topacio-ink uppercase">{(r.lang || "es")}</span>
                        {r.review_wa_sent && <span className="badge-brutal bg-topacio-green text-white !text-[9px] ml-1.5">WA</span>}
                        <p className="text-[10px] text-topacio-faded mt-1">{(r.review_sent_at || "").slice(0, 10)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {canEdit && (
              <div className="card-brutal overflow-hidden h-fit">
                <div className="bg-topacio-ink px-6 py-4">
                  <h3 className="font-display font-bold text-white text-sm flex items-center gap-2 uppercase tracking-wide">
                    <Sparkles className="w-4 h-4 text-brand" /> Fotos de temporada
                  </h3>
                </div>
                <div className="p-6">
                  <p className="text-sm text-topacio-faded leading-relaxed">
                    Regenera las 4 fotos de muestra de la galería pública con IA: mismas rutas (Teide, Masca, Anaga, costa sur) con la luz y la ropa de la temporada que elijas.
                  </p>
                  <div className="flex flex-wrap items-end gap-3 mt-5">
                    <div>
                      <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Temporada</label>
                      <select data-testid="demo-season-select" value={season} onChange={(e) => setSeason(e.target.value)} className="input-brutal mt-1 !w-44">
                        <option value="verano">Verano</option>
                        <option value="primavera">Primavera</option>
                        <option value="otono">Otoño</option>
                        <option value="invierno">Invierno</option>
                      </select>
                    </div>
                    <button data-testid="demo-refresh-btn" disabled={genBusy || reviewStats.gen?.status === "running"} onClick={regenerateDemo} className="btn-primary !text-xs">
                      {(genBusy || reviewStats.gen?.status === "running") ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      Regenerar fotos
                    </button>
                  </div>
                  {reviewStats.gen?.status === "running" && (
                    <p data-testid="demo-gen-running" className="mt-4 text-xs font-bold text-topacio-faded flex items-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generando ({reviewStats.gen.season})… suele tardar 1-2 minutos.
                    </p>
                  )}
                  {reviewStats.gen?.status === "done" && (
                    <p data-testid="demo-gen-done" className="mt-4 text-xs font-bold text-topacio-green">
                      Última generación: {reviewStats.gen.season} · {(reviewStats.gen.at || "").slice(0, 16).replace("T", " ")}
                    </p>
                  )}
                  {reviewStats.gen?.status === "error" && (
                    <p data-testid="demo-gen-error" className="mt-4 text-xs font-bold text-topacio-red">Error en la última generación: {reviewStats.gen.error}</p>
                  )}
                </div>
              </div>
              )}
            </div>

            {canEdit && (
            <div className="card-brutal overflow-hidden mt-6">
              <div className="bg-topacio-ink px-6 py-4 flex items-center justify-between">
                <h3 className="font-display font-bold text-white text-sm flex items-center gap-2 uppercase tracking-wide">
                  <FaGoogle className="w-4 h-4 text-brand" /> Reseñas reales de Google
                </h3>
                <span data-testid="google-key-status" className={`badge-brutal ${googleKey.configured ? "bg-topacio-green text-white" : "bg-zinc-200 text-topacio-faded"}`}>
                  {googleKey.configured ? `Activa ${googleKey.masked || ""}` : "Sin configurar"}
                </span>
              </div>
              <div className="p-6">
                <p className="text-sm text-topacio-faded leading-relaxed">
                  Pega aquí tu API key de Google Cloud (con «Places API (New)» activada) y las reseñas reales de vuestra ficha aparecerán solas en la web. Buscamos el negocio «Rent a Moto Topacio, Costa Adeje» automáticamente y actualizamos las reseñas cada 6 horas.
                </p>
                <div className="flex flex-wrap items-end gap-3 mt-5">
                  <div className="flex-1 min-w-[220px]">
                    <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Google Places API key</label>
                    <input data-testid="google-key-input" type="password" value={googleKeyInput}
                      onChange={(e) => setGoogleKeyInput(e.target.value)}
                      placeholder={googleKey.configured ? googleKey.masked : "AIza…"}
                      className="input-brutal mt-1" />
                  </div>
                  <button data-testid="google-key-save-btn" onClick={saveGoogleKey} className="btn-primary !text-xs">
                    <KeyRound className="w-4 h-4" /> Guardar clave
                  </button>
                  {googleKey.configured && (
                    <button data-testid="google-key-clear-btn" onClick={async () => { setGoogleKeyInput(""); await axios.post(`${API}/admin/settings/google-key`, { api_key: "" }, { headers }); toast.success("Clave eliminada"); load(); }} className="btn-secondary !text-xs">
                      Quitar
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-topacio-faded mt-4 font-semibold">
                  Cómo obtenerla: console.cloud.google.com → crear proyecto → activar «Places API (New)» → Credenciales → Crear clave de API.
                </p>
              </div>
            </div>
            )}
          </>
        )}

        {tab === "customers" && (
          <CustomersTab headers={headers} role={role} />
        )}

        {tab === "calendar" && (
          <CalendarTab headers={headers} canEdit={canEdit} />
        )}
        {tab === "content" && (
          <ContentTab headers={headers} canEdit={canEdit} />
        )}

        {tab === "partners" && (
          <PartnersTab headers={headers} canEdit={canEdit} />
        )}

        {tab === "logs" && role === "superadmin" && (
          <LogsTab headers={headers} />
        )}

        {tab === "users" && canEdit && (
          <>
            <h2 className="font-display text-xl font-bold text-topacio-ink mb-8">Usuarios del panel</h2>
            <div className="card-brutal p-6 mb-8">
              <h3 className="font-display text-base font-bold text-topacio-ink mb-5">Crear acceso</h3>
              <div className="flex flex-wrap gap-3 items-end">
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Nombre</label>
                  <input data-testid="user-name-input" value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="input-brutal mt-1 !w-44" />
                </div>
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Email</label>
                  <input data-testid="user-email-input" type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="input-brutal mt-1 !w-56" />
                </div>
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Contraseña</label>
                  <input data-testid="user-password-input" type="text" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="mín. 8 caracteres" className="input-brutal mt-1 !w-44" />
                </div>
                <div>
                  <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Rol</label>
                  <select data-testid="user-role-select" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="input-brutal mt-1 !w-56">
                    <option value="viewer">Consulta (solo lectura)</option>
                    <option value="vendedor">Vendedor (reservas de tienda)</option>
                    <option value="entrega">Entrega (parte + firma móvil)</option>
                    <option value="mecanico">Mecánico (marca «revisada»)</option>
                    <option value="lavamotos">Lavamotos (marca «OK»)</option>
                    {isSuper && <option value="admin">Administrador</option>}
                  </select>
                </div>
                <button data-testid="user-create-btn" onClick={createStaff} disabled={!newUser.name || !newUser.email || !newUser.password} className="btn-primary !px-5 !py-3 !text-xs">
                  <Plus className="w-4 h-4" /> Crear usuario
                </button>
              </div>
              {!isSuper && (
                <p className="text-[11px] text-topacio-faded font-semibold mt-3">Solo el superadministrador puede crear o eliminar administradores.</p>
              )}
            </div>
            <div className="card-brutal overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="bg-topacio-ink text-left text-xs uppercase tracking-wider text-white">
                    {["Nombre", "Email", "Rol", "Estado", "Alta", "Acciones"].map((h) => (
                      <th key={h} className="px-5 py-4 font-bold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {staffUsers.map((u) => (
                    <tr key={u.email} data-testid={`staff-row-${u.email}`} className={`hover:bg-topacio-sand transition-colors ${u.active === false ? "opacity-50" : ""}`}>
                      <td className="px-5 py-4 font-bold text-topacio-ink">{u.name || "—"}</td>
                      <td className="px-5 py-4 text-topacio-faded">{u.email}</td>
                      <td className="px-5 py-4">
                        <span className={`badge-brutal ${ROLE_COLORS[u.role] || "bg-white text-topacio-ink"}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span data-testid={`staff-status-${u.email}`} className={`badge-brutal ${u.active !== false ? "bg-topacio-green text-white" : "bg-zinc-300 text-topacio-faded"}`}>
                          {u.active !== false ? "Activo" : "Bloqueado"}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-topacio-faded text-xs">{(u.created_at || "").slice(0, 10)}</td>
                      <td className="px-5 py-4">
                        <div className="flex gap-2">
                          <button data-testid={`staff-edit-${u.email}`} onClick={() => setEditingUser(u)} title="Editar (nombre, rol, contraseña, acceso)"
                            className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-brand transition-colors">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          {isSuper && u.role !== "superadmin" && (
                            <button data-testid={`staff-delete-${u.email}`} onClick={() => deleteStaff(u.email)} title="Eliminar usuario"
                              className="w-8 h-8 bg-white border-2 border-topacio-ink rounded-lg flex items-center justify-center hover:bg-topacio-red hover:text-white transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>

      {editorOpen && (
        <BikeEditor bike={editing} groups={groups} onClose={() => setEditorOpen(false)} onSave={saveBike}
          onManageGroups={() => { setEditorOpen(false); setTab("groups"); }} headers={headers} />
      )}
      {manualOpen && (
        <ManualBookingForm bikes={bikes} headers={headers} onClose={() => setManualOpen(false)} onSave={saveManualBooking} />
      )}
      {helpOpen && <HelpModal role={role} onClose={() => setHelpOpen(false)} />}
      {statusChange && (
        <BikeStatusModal change={statusChange} setChange={setStatusChange} busy={statusBusy}
          onClose={() => setStatusChange(null)} onSave={confirmBikeStatus} />
      )}
      {commissionFor && (
        <CommissionModal booking={commissionFor} headers={headers} onClose={() => setCommissionFor(null)} onSaved={load} />
      )}
      {depositFor && (
        <DepositModal booking={depositFor} headers={headers} onClose={() => setDepositFor(null)} onSaved={load} />
      )}
      {detailRef && (
        <BookingDetailsModal bookingRef={detailRef} headers={headers} onClose={() => setDetailRef(null)} />
      )}
      {editingUser && (
        <UserEditModal user={editingUser} headers={headers} isSuper={isSuper}
          selfEmail={localStorage.getItem("admin_email") || ""}
          onClose={() => setEditingUser(null)} onSaved={load} />
      )}
      <SiempriaCredit />
    </div>
  );
}
